import * as React from "react";
import { useState, useEffect } from "react";
import axiosInstance from "../../AxiosInstance";
import { URLS } from "../../API/API endpoints";
import { useSelector, useDispatch } from "react-redux";
import { createOrder } from "../../Redux/Action/createposorderAction";
import { useTranslation } from "react-i18next";
import { storeSelectedPaymentMethod } from "../../Redux/Action/paymentAction";
import { toast } from "react-toastify";
import i18next from "i18next";
import { detectWebDeviceType } from "../../utils";
import SelectBox from "../CustomWidgets/SelectBox";

/* ─────────────────────────────────────────────
   Inline styles injected once
───────────────────────────────────────────── */
const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap');

  .pm-modal * { box-sizing: border-box; }
  .pm-modal { font-family: 'DM Sans', sans-serif; }

  .pm-spinner { display: flex; gap: 8px; }
  .pm-spinner span {
    width: 12px; height: 12px; background: #fff; border-radius: 50%;
    animation: pm-bounce 0.9s ease-in-out infinite;
  }
  .pm-spinner span:nth-child(2) { animation-delay: 0.15s; }
  .pm-spinner span:nth-child(3) { animation-delay: 0.30s; }
  @keyframes pm-bounce {
    0%,80%,100% { transform: scale(0.6); opacity: 0.5; }
    40%          { transform: scale(1);   opacity: 1;   }
  }

  .pm-key { transition: transform 0.12s ease, box-shadow 0.12s ease; }
  .pm-key:not(:disabled):hover  { transform: translateY(-2px); box-shadow: 0 6px 18px rgba(0,0,0,0.12); }
  .pm-key:not(:disabled):active { transform: translateY(0); box-shadow: none; }

  .pm-pill { transition: all 0.18s ease; }
  .pm-pill:hover:not(.pm-pill--disabled) {
    border-color: #1b4597 !important; color: #1b4597 !important;
    box-shadow: 0 4px 14px rgba(27,69,151,0.15); transform: translateY(-1px);
  }

  .pm-order-btn { transition: all 0.18s ease; }
  .pm-order-btn:hover { filter: brightness(1.08); transform: translateY(-2px); box-shadow: 0 8px 20px rgba(0,0,0,0.15); }

  .pm-done-btn { transition: all 0.2s ease; position: relative; overflow: hidden; }
  .pm-done-btn::after {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(135deg, rgba(255,255,255,0.15) 0%, transparent 60%);
    pointer-events: none;
  }
  .pm-done-btn:not(:disabled):hover {
    transform: translateY(-2px); box-shadow: 0 10px 30px rgba(27,69,151,0.40); filter: brightness(1.08);
  }

  .pm-input-no-arrows::-webkit-inner-spin-button,
  .pm-input-no-arrows::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
  .pm-input-no-arrows { -moz-appearance: textfield; }

  .pm-clear-btn { transition: all 0.18s ease; }
  .pm-clear-btn:not(.pm-clear-btn--disabled):hover {
    filter: brightness(1.1); transform: translateY(-1px); box-shadow: 0 6px 18px rgba(244,32,32,0.30);
  }

  .pm-card {
    background: #fff; border-radius: 16px;
    border: 1px solid #EBEBF0; box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  }

  .pm-discount-toggle-btn { transition: all 0.18s ease; }
  .pm-discount-toggle-btn:hover { filter: brightness(1.05); }
`;

function injectStyles() {
  if (document.getElementById("pm-global-styles")) return;
  const tag = document.createElement("style");
  tag.id = "pm-global-styles";
  tag.textContent = GLOBAL_STYLES;
  document.head.appendChild(tag);
}

/* ─────────────────────────────────────────────
   Helper: which methods get the cash add-on.
───────────────────────────────────────────── */
const CASH_ADDON_EXCLUDED = new Set(["Cash", "Cash & Bank", "COD", "Credit"]);
const supportsCashAddon = (methodName) =>
  Boolean(methodName && !CASH_ADDON_EXCLUDED.has(methodName));

/* ─────────────────────────────────────────────
   Sub-components
───────────────────────────────────────────── */
const LoadingSpinner = () => (
  <div style={{ zIndex: 50 }} className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black bg-opacity-50">
    <div className="pm-spinner"><span /><span /><span /></div>
  </div>
);

function SectionLabel({ children }) {
  return (
    <p style={{ fontSize: 17, fontWeight: 600, letterSpacing: "0.02em", color: "#494949", marginBottom: 3 }}>
      {children}
    </p>
  );
}

function ValueRow({ label, value, highlight }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "10px 16px", borderRadius: 10, marginBottom: 8,
      background: highlight ? "linear-gradient(135deg,#EEF3FF 0%,#F5F8FF 100%)" : "#FAFAFA",
      border: highlight ? "1px solid #C7D6F8" : "1px solid #F0F0F0",
    }}>
      <span style={{ fontSize: 16, color: "#494949", fontWeight: 500 }}>{label}</span>
      <span style={{ fontSize: 20, fontWeight: 700, color: highlight ? "#1b4597" : "#181818", direction: "ltr" }}>
        {value}
      </span>
    </div>
  );
}

function OrderFromButton({ paymentMethod, selected, onClick, disabled }) {
  const { t } = useTranslation();
  const name = paymentMethod.name === "self_delivery" ? t("Self Delivery") : t(paymentMethod.name);
  return (
    <div className="pm-order-btn" onClick={onClick} style={{
      display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
      marginTop: 10, padding: "14px 24px", fontSize: 17, fontWeight: 600, borderRadius: 14,
      border: selected ? "3px solid rgba(255,255,255,0.6)" : "2px solid rgba(255,255,255,0.25)",
      boxShadow: selected ? "0 0 0 3px rgba(27,69,151,0.30), 0 6px 20px rgba(0,0,0,0.18)" : "0 2px 8px rgba(0,0,0,0.10)",
      cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
      backgroundColor: paymentMethod?.background_color || "#7EC242",
      color: paymentMethod?.text_color || "#fff", whiteSpace: "nowrap",
    }}>
      {name}
    </div>
  );
}

function PaymentButton({ paymentMethod, selected, onClick, disabled }) {
  const { t } = useTranslation();
  const nameMap = {
    Cash: t("Cash"), Bank: t("Bank"), COD: t("COD"),
    "Cash & Bank": t("Cash & Bank"), "Credit Card": t("Credit Card"),
    InstaPay: t("InstaPay"), Credit: t("Credit"),
  };
  return (
    <div
      className={`pm-pill${disabled ? " pm-pill--disabled" : ""}`}
      onClick={!disabled ? onClick : undefined}
      style={{
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        padding: "10px 8px", minHeight: 56, borderRadius: 12, textAlign: "center",
        border: selected ? "2.5px solid #1b4597" : "1.5px solid #E2E5EB",
        background: selected ? "linear-gradient(135deg,#EEF3FF 0%,#F0F5FF 100%)" : "#FAFAFA",
        color: selected ? "#1b4597" : "#494949", fontWeight: selected ? 700 : 500,
        fontSize: 18, lineHeight: 1.3, cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        boxShadow: selected ? "0 4px 14px rgba(27,69,151,0.14)" : "none",
      }}
    >
      {nameMap[paymentMethod.name] || t(paymentMethod.name)}
    </div>
  );
}

function KeypadButton({ value, onClick, children, disabled }) {
  const isGreen = ["10", "20", "50", "100", "200"].includes(String(value));
  return (
    <button
      type="button" className="pm-key"
      onClick={disabled ? null : onClick}
      disabled={disabled}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        height: 64, width: 148, marginInlineEnd: 10, borderRadius: 12,
        fontSize: 22, fontWeight: 600,
        border: isGreen ? "none" : "1px solid #E2E5EB",
        background: isGreen
          ? "linear-gradient(135deg,#7EC242 0%,#6BB035 100%)"
          : "linear-gradient(180deg,#FFFFFF 0%,#F5F5F5 100%)",
        color: isGreen ? "#fff" : "#1F2937",
        boxShadow: isGreen ? "0 4px 14px rgba(126,194,66,0.35)" : "0 2px 6px rgba(0,0,0,0.07)",
        cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.4 : 1,
        letterSpacing: "0.02em",
      }}
    >
      {children || value}
    </button>
  );
}

/* ─────────────────────────────────────────────
   ORDER-LEVEL DiscountCard component
───────────────────────────────────────────── */
function DiscountCard({
  t,
  discountType,
  setDiscountType,
  discountValue,
  setDiscountValue,
  maxDiscount,
  originalTotal,
  discountAmount,
  discountPercent,
  billDiscountAmount = 0
}) {
  console.log("BILL DISCOUNT AMOUNT", billDiscountAmount)
  const [discountError, setDiscountError] = useState("");

  const totalDiscount = discountAmount + (billDiscountAmount || 0);


  const handleTypeSwitch = (type) => {
    setDiscountType(type);
    setDiscountValue("");
    setDiscountError("");
  };

  const handleDiscountInput = (val) => {
    const sanitized = val.replace(/[^0-9.]/g, "");
    if (!/^\d*\.?\d{0,2}$/.test(sanitized)) return;

    const num = parseFloat(sanitized) || 0;
    const origTotal = parseFloat(String(originalTotal).replace(/[^0-9.]/g, "")) || 0;

    const EPSILON = 0.001;

    if (discountType === "percentage") {
      // Compare directly in percentage-space — converting to currency and
      // back (as before) double-rounds and can let an "allowed" percentage
      // map to a currency amount over the real limit.
      const exactMaxPct = origTotal > 0 ? (maxDiscount / origTotal) * 100 : 0;

      if (num > exactMaxPct + EPSILON) {
        // Floor (never round up) so the displayed max is always safe to
        // type back in — rounding up here is what caused the original report.
        const maxPctDisplay = Math.floor(exactMaxPct * 10) / 10;
        setDiscountError(`Max ${maxPctDisplay.toFixed(1)}%`);
      } else {
        setDiscountError("");
      }
    } else {
      // Currency mode: compare both sides rounded to cents, as fixed earlier.
      const roundedNum = Math.round(num * 100) / 100;
      const roundedMax = Math.round(maxDiscount * 100) / 100;

      if (roundedNum > roundedMax + EPSILON) {
        setDiscountError(`Max EGP ${roundedMax.toFixed(2)}`);
      } else {
        setDiscountError("");
      }
    }

    setDiscountValue(sanitized);
  };

  const clearDiscount = () => {
    setDiscountValue("");
    setDiscountType("");
    setDiscountError("");
  };

  const origTotal = parseFloat(String(originalTotal).replace(/[^0-9.]/g, "")) || 0;
  const totalDiscountPercent =
    origTotal > 0 ? (totalDiscount / origTotal) * 100 : 0;
  const afterDiscount = Math.max(origTotal - discountAmount, 0);

  return (
    <div className="pm-card" style={{ padding: "14px 16px", marginBottom: 10 }}>
      {/* ── Header row ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{
            width: 28, height: 28, borderRadius: 8,
            background: "linear-gradient(135deg,#FFF7ED,#FEF3C7)",
            border: "1px solid #FDE68A",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="6" cy="6" r="3" /><circle cx="6" cy="18" r="3" />
              <line x1="20" y1="4" x2="8.12" y2="15.88" />
              <line x1="14.47" y1="14.48" x2="20" y2="20" />
              <line x1="8.12" y1="8.12" x2="12" y2="12" />
            </svg>
          </div>
          <SectionLabel>{t("Order Discount")}</SectionLabel>
        </div>

        <div style={{ display: "flex", background: "#F0F2F7", borderRadius: 10, padding: 3, gap: 2 }}>
          {["value", "percentage"].map((type) => (
            <button
              key={type}
              className="pm-discount-toggle-btn"
              onClick={() => handleTypeSwitch(type)}
              style={{
                padding: "5px 16px", borderRadius: 8, border: "none",
                fontSize: 13, fontWeight: 600, cursor: "pointer",
                background: discountType === type
                  ? "linear-gradient(135deg,#1b4597,#2a5ec7)"
                  : "transparent",
                color: discountType === type ? "#fff" : "#6B7280",
                boxShadow: discountType === type ? "0 2px 8px rgba(27,69,151,0.25)" : "none",
              }}
            >
              {type === "value" ? t("Amount") : "%"}
            </button>
          ))}
        </div>
      </div>

      {/* ── Input row ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div
          className="pm-card"
          style={{
            display: "flex", alignItems: "center", flex: 1, height: 52, padding: "0 14px", gap: 8,
            border: discountError
              ? "2px solid #F42020"
              : (discountValue && !discountError)
                ? "2px solid #D97706"
                : "1.5px solid #E2E5EB",
            boxShadow: (discountValue && !discountError) ? "0 0 0 3px rgba(217,119,6,0.10)" : "none",
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 700, flexShrink: 0, color: discountError ? "#F42020" : "#D97706" }}>
            {discountType === "percentage" ? "%" : "EGP"}
          </span>
          <input
            type="number"
            className="pm-input-no-arrows"
            placeholder="0.00"
            disabled={!discountType}
            value={discountValue}
            onChange={(e) => handleDiscountInput(e.target.value)}
            style={{
              flex: 1, border: "none", outline: "none", background: "transparent",
              fontSize: 20, fontWeight: 700, color: "#1F2937",
              direction: "ltr", textAlign: "left",
            }}
            maxLength={7}
          />
          {discountValue ? (
            <button
              onClick={clearDiscount}
              style={{
                border: "none", background: "none", cursor: "pointer",
                color: "#9CA3AF", fontSize: 18, padding: "0 4px", lineHeight: 1,
                display: "flex", alignItems: "center",
              }}
            >×</button>
          ) : null}
        </div>
      </div>

      {/* ── Error ── */}
      {discountError && (
        <div style={{
          display: "flex", alignItems: "center", gap: 6,
          marginTop: 6, padding: "6px 10px",
          background: "#FEF2F2", borderRadius: 8, border: "1px solid #FECACA",
        }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2.5">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span style={{ fontSize: 12, color: "#DC2626", fontWeight: 600 }}>
            {discountError} — can't reduce total below cost price
          </span>
        </div>
      )}

      {/* ── Live preview ── */}
      {totalDiscount > 0 && !discountError && (
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          marginTop: 10, padding: "10px 14px",
          background: "linear-gradient(135deg,#FFFBEB,#FFFDE7)",
          borderRadius: 12, border: "1px solid #FDE68A",
        }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontSize: 11, color: "#92400E", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              {t("Discount Applied")}
            </span>
            <span style={{ fontSize: 14, color: "#B45309", fontWeight: 500 }}>
              − EGP {discountAmount.toFixed(2)}
              {discountType === "percentage" &&
                <span style={{ marginLeft: 6, color: "#92400E", fontSize: 12 }}>
                  ({discountPercent.toFixed(1)}%)
                </span>
              }

            </span>
          </div>
          <div style={{ textAlign: "right" }}>
            <span style={{ fontSize: 11, color: "#166534", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", display: "block" }}>
              {t("New Total")}
            </span>
            <span style={{ fontSize: 18, fontWeight: 700, color: "#166534", }}>
              EGP {afterDiscount.toFixed(2)}
            </span>
          </div>
        </div>
      )}

      {/* ── Max discount hint ── */}
      <p style={{ fontSize: 16, color: "#9CA3AF", marginTop: 7, fontWeight: 500 }}>
        {t("Max allowed discount")}: EGP {maxDiscount.toFixed(2)}
      </p>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Main Payment_Modal
───────────────────────────────────────────── */
function Payment_Modal({
  onClose, isCompanyCheckout, grandTotal, handleCheckout,
  setTenderCash, creditAmount, setCreditAmount,
  deliveryCheck, deliveryFee, setDeliveryFee,
  returnDeliveryFee, setReturnDeliveryFee, showReturnDelivery,
  constReturnDeliveryFee, discountType, setDiscountType,
  discountValue, setDiscountValue, originalTotal,
  totalBillAmount, totalCostPrice, nonReturnTotal, billDiscountAmount = 0
}) {
  injectStyles();

  console.log("BILL DISCOUNT AMOUNT", billDiscountAmount)

  const { t, i18n } = useTranslation();
  const deviceType = detectWebDeviceType();
  const { user } = useSelector((state) => state?.login);
  const dispatch = useDispatch();

  // ─── existing state ───────────────────────────────────────────────────────
  const [totalPaid, setTotalPaid] = useState("");
  const [change, setChange] = useState("");
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState();
  const [isBankPaymentSelected, setIsBankPaymentSelected] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [orderFromOptions, setOrderFromOptions] = useState([]);
  const [allSalesman, setAllSalesman] = useState([]);
  const [selectedSalesman, setSelectedSalesman] = useState(null);
  const [allDeliveryBoys, setAllDeliveryBoys] = useState([])
  const [selectedDeliveryBoys, setSelectedDeliveryBoys] = useState(null)
  const [selectedOrderFrom, setSelectedOrderFrom] = useState();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [activePaymentIndex, setActivePaymentIndex] = useState(0);
  const [activeOrderFromIndex, setActiveOrderFromIndex] = useState("");
  const [cashPaid, setCashPaid] = useState(0);
  const [remainingAmount, setRemainingAmount] = useState(grandTotal);

  // ─── CASH ADD-ON state ────────────────────────────────────────────────────
  const [cashAmount, setCashAmount] = useState("");
  const [methodAmount, setMethodAmount] = useState(grandTotal);

  useEffect(() => {
    const cash = parseFloat(cashAmount) || 0;
    const gt = parseFloat(String(grandTotal).replace(/[^0-9.]/g, "")) || 0;
    setMethodAmount(parseFloat(Math.max(gt - cash, 0).toFixed(2)));
  }, [cashAmount, grandTotal]);

  const resetCashAddon = () => {
    setCashAmount("");
    setTenderCash("");
    setMethodAmount(parseFloat(String(grandTotal).replace(/[^0-9.]/g, "")) || 0);
  };

  // ─── ORDER-LEVEL DISCOUNT helpers ────────────────────────────────────────
  // origTotal  = grandTotal BEFORE order-level discount (passed as originalTotal prop)
  // grandTotal = already reflects product-line discounts (passed from parent)
  // So order-level discount is applied ON TOP of grandTotal.
  const origTotal = parseFloat(String(originalTotal).replace(/[^0-9.]/g, "")) || 0;
  const costPrice = parseFloat(String(totalCostPrice).replace(/[^0-9.]/g, "")) || 0;
  const maxDiscount = Math.max(nonReturnTotal - costPrice, 0);
  const rawBillDiscount = Number(billDiscountAmount) || 0;
  const cappedBillDiscount = maxDiscount > 0
    ? Math.min(rawBillDiscount, maxDiscount)
    : 0; // if maxDiscount is 0, no discount is allowed at all
  console.log('nonReturnTotal==>', nonReturnTotal);

  const orderDiscountAmount =
    discountType === "percentage"
      ? ((parseFloat(discountValue) || 0) / 100) * origTotal
      : (parseFloat(discountValue) || 0);
  const orderDiscountPercent =
    discountType === "value"
      ? origTotal > 0 ? ((parseFloat(discountValue) || 0) / origTotal) * 100 : 0
      : (parseFloat(discountValue) || 0);


  const billDiscountAmt = Number(billDiscountAmount) || 0;
  const combinedDiscountAmount = orderDiscountAmount + billDiscountAmt;
  const combinedDiscountPercent =
    origTotal > 0 ? (combinedDiscountAmount / origTotal) * 100 : 0;

  // ── effectiveTotal: what the customer actually owes after order-level discount ──
  const effectiveTotal = Number(
    (
      (parseFloat(String(grandTotal).replace(/[^0-9.-]/g, "")) || 0) -
      orderDiscountAmount
    ).toFixed(2)
  );

  // ──────────────────
  // ───────────────────────────────────────────────────────

  const isETAenabled = useSelector((s) => s?.permissionReducer?.permissions?.is_eta_enabled);
  const returnProducts = useSelector((s) => s.returnProductReducer.products);
  const localProducts = useSelector((s) => s.products.products);
  const getCustomerState = useSelector((s) => s?.getcustomer?.selectedCustomer);
  const showSalesmanSelection = useSelector((s) => s?.permissionReducer?.permissions?.show_salesman_selection);
  const showDeliveryBoySelection = useSelector((s) => s?.permissionReducer?.permissions?.show_delivery_boy_selection);
  const user_id = useSelector((s) => s?.login?.user?.result?.user_id);
  const allowBillDiscount = useSelector(
    (state) => state?.permissionReducer?.permissions?.is_allow_bill_discount,
  );
  const totalPaidInputRef = React.useRef(null);

  const calculateTotalTax = (products) =>
    products.reduce((total, product) => {
      const price = Number(product.price_after_discount) || 0;
      const qty = Number(product.quantity) || 1;
      const taxPct = isETAenabled && product?.vat_details?.tax_id
        ? Number(product?.vat_details?.tax_rate_percent) || 0 : 0;
      return total + (price * qty * taxPct) / (100 + taxPct);
    }, 0);
  const totalTax = calculateTotalTax(localProducts);

  /* ── API calls ── */
  const getPaymentMethod = async (userID) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(URLS.PAYMENT_METHODS, { user_id: userID });
      if (response?.data.result.data != null) {
        const methods = response.data.result.data.map((m) => ({
          name: m.name, id: m.id, is_cash_bank: m.is_cash_bank,
          cash_id: m.cash_payment_method_id,
          bank_id: m.bank_id ?? m.bank_payment_method_id,
        }));
        setPaymentMethods([...methods]);
        let selectedIndex;
        if (grandTotal <= 0 && returnProducts?.[0]?.payment_method_id) {
          selectedIndex = methods.findIndex((m) => {
            const sameId = m.id === returnProducts?.[0]?.payment_method_id;
            const hasBankId = returnProducts?.[0]?.bank_id != null && m.bank_id != null;
            if (sameId && hasBankId) return m.bank_id === returnProducts?.[0]?.bank_id;
            console.log("sameId=====>", returnProducts?.[0]?.payment_method_id);

            return sameId;
          });
        } else {
          selectedIndex = methods.findIndex((m) => m.name === "Cash");
        }
        console.log('selectedIndex==>', selectedIndex);

        if (selectedIndex !== -1) {
          const sel = methods[selectedIndex];
          handlePaymentMethodClick(sel, selectedOrderFrom);
          setSelectedPaymentMethod(sel);
          setActivePaymentIndex(selectedIndex);
        }
      }
    } catch (err) { console.error(err); setErrorMessage("Failed to fetch payment methods."); }
    finally { setLoading(false); }
  };

  const getOrderFrom = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(URLS.ORDER_FROM, {
        user_id, lang: i18next.language === "ar" ? "ar_001" : "en_US",
      });
      const posOrderFrom = response?.data?.result?.[0]?.pos_order_from;
      setOrderFromOptions(posOrderFrom);
      setSelectedOrderFrom(posOrderFrom[0]);
      setActiveOrderFromIndex(0);
      getOrderFromMethods(posOrderFrom[0]?.id, posOrderFrom[0]);
    } catch (err) { console.error(err); setErrorMessage("Failed to fetch order from options."); }
    finally { setLoading(false); }
  };

  const getSalesman = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(URLS.GET_ALL_SALESMAN, {
        lang: i18next.language === "ar" ? "ar_001" : "en_US",
        company_id: user?.result?.company_id, search: "", limit: 100, offset: 0,
      });
      setAllSalesman(response?.data?.result?.status_code == 200 ? response?.data?.result?.data : []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };
  const getDeliveryBoys = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(URLS.GET_CASHIER_DELIVERY_BOYS, {
        company_id: user?.result?.company_id, search: "", limit: 100, offset: 0,
      });
      setAllDeliveryBoys(response?.data?.result?.status_code == 200 ? response?.data?.result?.data : []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const getOrderFromMethods = async (id, company) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(URLS.ORDER_FROM_METHOD, { user_id, pos_order_from_id: id });
      if (response?.data?.result?.status_code == 200) {
        const data = response?.data?.result?.data;
        setPaymentMethods(data);
        const cashIndex = data?.findIndex((m) => m.name === "Cash");
        if (cashIndex !== -1) {
          handlePaymentMethodClick(data[cashIndex], company);
          setSelectedPaymentMethod(data[cashIndex]);
          setActivePaymentIndex(cashIndex);
        } else {
          handlePaymentMethodClick(data[0], company);
        }
      } else { setPaymentMethods([]); }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const getCreditAmount = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(URLS.GET_CUSTOMER_CREDIT, { phone: getCustomerState?.mobile });
      if (response?.data?.result[0].status_code === 200) setCreditAmount(response?.data?.result[0]);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (isCompanyCheckout) getOrderFrom(); }, [user?.result?.user_id]);
  useEffect(() => {
    if (!deliveryCheck && !isCompanyCheckout) getPaymentMethod(user?.result?.user_id);
    getSalesman();
    if (getCustomerState) getCreditAmount();
  }, [user?.result?.user_id, getCustomerState]);
  useEffect(() => {
    if (!deliveryCheck && !isCompanyCheckout) getPaymentMethod(user?.result?.user_id);
    getDeliveryBoys();
    if (getCustomerState) getCreditAmount();
  }, [user?.result?.user_id, getCustomerState]);

  useEffect(() => {
    if (grandTotal < 0) { setTotalPaid(grandTotal.toString()); setIsBankPaymentSelected(true); }
  }, [grandTotal]);

  // Re-compute change whenever order-level discount changes while totalPaid is already filled
  useEffect(() => {
    if (totalPaid) {
      const paid = parseFloat(totalPaid) || 0;
      setChange(`${Math.max(paid - effectiveTotal, 0)}`);
      if (selectedPaymentMethod?.name === "Cash & Bank")
        setRemainingAmount(Math.max(effectiveTotal - paid, 0));
    }
  }, [orderDiscountAmount]);

  /* ── Keypad layout ── */
  const keypadValues = [
    [{ value: "1" }, { value: "2" }, { value: "3" }, { value: "10" }],
    [{ value: "4" }, { value: "5" }, { value: "6" }, { value: "20" }],
    [{ value: "7" }, { value: "8" }, { value: "9" }, { value: "50" }],
    [{ value: "." }, { value: "0" }, { value: "200" }, { value: "100" }],
  ];

  /* ── Keypad handler ── */
  const handleKeypadButtonClick = (value) => {
    const usesCashAddon = supportsCashAddon(selectedPaymentMethod?.name);

    if (usesCashAddon) {
      const gt = parseFloat(String(grandTotal).replace(/[^0-9.]/g, "")) || 0;
      if (value === "C") { setCashAmount(""); setTenderCash(""); return; }
      if (value === ".") {
        if (!cashAmount.includes(".")) setCashAmount((p) => p + ".");
        return;
      }
      if (!isNaN(parseInt(value, 10))) {
        const isQuickAdd = ["10", "20", "50", "100", "200"].includes(value);
        if (isQuickAdd) {
          const upd = Math.min(parseFloat(cashAmount || 0) + parseFloat(value), gt);
          setCashAmount(upd.toString()); setTenderCash(upd.toString());
        } else {
          if (cashAmount.length >= 10) return;
          const upd = cashAmount + value;
          if ((parseFloat(upd) || 0) <= gt) { setCashAmount(upd); setTenderCash(upd); }
        }
      }
      setErrorMessage(""); return;
    }

    if (totalPaid.length >= 10 && value !== "C" && value !== "." && !["50", "100", "200"].includes(value)) return;

    if (value === "C") {
      setTotalPaid(""); setTenderCash(0); setChange("");
    } else if (value === ".") {
      if (!totalPaid.includes(".")) { setTotalPaid((p) => p + "."); setTenderCash((p) => String(p) + "." || 0); }
    } else if (!isNaN(parseInt(value, 10))) {
      // ── Use effectiveTotal (grandTotal − orderDiscount) for change calculation ──
      if (["50", "100", "200", "20", "10"].includes(value)) {
        const upd = parseFloat(totalPaid || 0) + parseFloat(value);
        setTotalPaid(upd.toString()); setTenderCash(upd.toString() || 0);
        setChange(`${Math.max(upd - effectiveTotal, 0)}`);
      } else {
        const upd = totalPaid + value;
        setTotalPaid(upd); setTenderCash(upd || 0);
        const num = parseFloat(upd.replace(/[^0-9.]/g, "")) || 0;
        setChange(`${Math.max(num - effectiveTotal, 0)}`);
        if (selectedPaymentMethod?.name === "Cash & Bank")
          setRemainingAmount(Math.max(effectiveTotal - num, 0));
      }
    }
    setErrorMessage("");
  };

  /* ── Payment method selection ── */
  const handlePaymentMethodClick = (method, company) => {
    resetCashAddon();
    if (grandTotal <= 0) setTotalPaid(0);
    if (company?.name === "self_delivery") {
      setDeliveryFee(company?.delivery_fee ?? 0);
    } else if (method.name === "COD") {
      setDeliveryFee(selectedOrderFrom?.delivery_fee ?? 0);
    } else {
      setDeliveryFee(null);
    }

    dispatch(storeSelectedPaymentMethod(method));
    setSelectedPaymentMethod(method);
    setActivePaymentIndex(paymentMethods.indexOf(method));

    if (method.name === "Cash & Bank") {
      const cashMethod = paymentMethods?.find((m) => m.name === "Cash");
      const bankMethod = paymentMethods?.find((m) => m.name === "Bank" || m.bank_id);
      if (cashMethod && bankMethod) {
        method = { name: method.name, id: { cashId: cashMethod.id, bankId: bankMethod.id } };
        setSelectedPaymentMethod(method);
      }
      setIsBankPaymentSelected(false); setCashPaid(0); setRemainingAmount(effectiveTotal);
      setTotalPaid(""); setChange("");
    } else if (supportsCashAddon(method.name)) {
      setIsBankPaymentSelected(false);
      setTotalPaid(String(grandTotal));
      setChange("0");
    } else if (method.name === "COD") {
      const cashMethod = paymentMethods.find((m) => m.name === "Cash");
      if (cashMethod) { method = { name: method.name, id: cashMethod.id }; setSelectedPaymentMethod(method); }
      setIsBankPaymentSelected(false); setTotalPaid(0); setTenderCash(0); setChange("0");
    } else if (method.name === "Credit") {
      setIsBankPaymentSelected(false); setTotalPaid(0); setTenderCash(0); setChange("0");
    } else {
      setIsBankPaymentSelected(false); setTenderCash(0); setTotalPaid(""); setChange("");
    }
    setErrorMessage("");
  };

  const handleOrderFromClick = (method) => {
    if (selectedOrderFrom?.id === method?.id) return;
    getOrderFromMethods(method?.id, method);
    setSelectedOrderFrom(method);
    setActiveOrderFromIndex(orderFromOptions.indexOf(method));
  };

  /* ── Build payment method for checkout ── */
  const buildPaymentMethodForCheckout = () => {
    const hasCashAddon = supportsCashAddon(selectedPaymentMethod?.name) && parseFloat(cashAmount) > 0;
    if (!hasCashAddon) return selectedPaymentMethod;
    const cashMethod = paymentMethods.find((m) => m.name === "Cash");
    return {
      ...selectedPaymentMethod,
      id: { cashId: cashMethod?.id, methodId: selectedPaymentMethod?.id },
      cashAmount: parseFloat(cashAmount),
      methodAmount: methodAmount,
    };
  };
  console.log("buildPaymentMethodForCheckout-===>", selectedPaymentMethod);

  /* ── Checkout click ── */
  const handleCheckoutClick = () => {
    if (errorMessage) {
      toast.error(t(`The Return Delivery Fee cannot exceed EGP ${constReturnDeliveryFee}`));
      return;
    }
    if (orderDiscountAmount > maxDiscount + 0.001) {
      toast.error(t("Discount exceeds maximum allowed amount"));
      return;
    }

    const discountPayload = {
      discount_type: discountType || null,
      discount_amount: parseFloat(orderDiscountAmount.toFixed(2)) || 0,
      discount_per: parseFloat(orderDiscountPercent.toFixed(2)) || 0,
    };

    handleCheckout(buildPaymentMethodForCheckout(), selectedOrderFrom, selectedSalesman, selectedDeliveryBoys, discountPayload,);
  };

  /* ── Clear ── */
  const handleClearButtonClick = () => {
    setTotalPaid(""); setChange(""); setRemainingAmount(0);
    setDiscountType(""); setDiscountValue(0);
    setCashAmount(""); setTenderCash("");
  };

  /* ── Keyboard shortcuts ── */
  const handleKeyPress = (event) => {
    const normalizedKey = { x: "ء" }[event.key] || event.key;
    if (event.key === "Enter") {
      event.preventDefault();
      if (selectedPaymentMethod?.name === "Cash & Bank" && totalPaid >= effectiveTotal) return;
      if (
        (selectedPaymentMethod?.name === "Cash & Bank" && totalPaid > 0) ||
        (selectedPaymentMethod?.name === "Cash" && totalPaid >= effectiveTotal) ||
        (selectedPaymentMethod?.name === "Bank" && totalPaid == effectiveTotal) ||
        selectedPaymentMethod?.name === "COD" ||
        selectedPaymentMethod?.name === "Credit" ||
        supportsCashAddon(selectedPaymentMethod?.name)
      ) handleCheckoutClick();
    } else if (event.shiftKey && normalizedKey === "Shift") {
      event.preventDefault();
      if (grandTotal <= 0) return;
      const nextIndex = (activePaymentIndex + 1) % paymentMethods.length;
      setActivePaymentIndex(nextIndex);
      setSelectedPaymentMethod(paymentMethods[nextIndex]);
      handlePaymentMethodClick(paymentMethods[nextIndex], selectedOrderFrom);
    } else if (event.ctrlKey && normalizedKey === "ء") {
      event.preventDefault();
      handleClearButtonClick();
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [totalPaid, cashAmount, grandTotal, effectiveTotal, selectedPaymentMethod, activePaymentIndex]);

  useEffect(() => {
    if (selectedPaymentMethod?.name === "Cash" || selectedPaymentMethod?.name === "Cash & Bank")
      totalPaidInputRef.current?.focus();
  }, [selectedPaymentMethod]);

  /* ── Helpers ── */
  const isRtl = i18n.language === "ar";
  const fmtMoney = (val) => {
    if (isRtl && val < 0) return `${(-val).toFixed(2)}-`;
    return parseFloat(val || 0).toFixed(2);
  };

  const showCashAddon = supportsCashAddon(selectedPaymentMethod?.name) && grandTotal > 0;
  const isKeypadDisabled = grandTotal < 0 || selectedPaymentMethod?.name === "COD" || selectedPaymentMethod?.name === "Credit";
  const isClearDisabled = selectedPaymentMethod?.name === "Credit" || selectedPaymentMethod?.name === "COD" || grandTotal < 0;
  const isDoneDisabled =
    (selectedPaymentMethod?.name === "Cash & Bank" && totalPaid <= 0) ||
    (selectedPaymentMethod?.name === "Cash" && totalPaid < effectiveTotal) ||
    (selectedPaymentMethod?.name === "Cash & Bank" && totalPaid >= effectiveTotal) ||
    methodAmount <= 0 ||
    orderDiscountAmount > maxDiscount + 0.001;

  console.log('totalPaid==>', totalPaid);
  console.log('effectiveTotal==>', effectiveTotal);
  // console.log('maxDiscount==>', effectiveTotal);

  /* ─────────────────────────────────────────────
     RENDER
  ───────────────────────────────────────────── */
  return (
    <div
      className="pm-modal"
      style={{
        position: "fixed", top: 20, left: 0, width: "100%", height: "100%",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 50, backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
      }}
    >
      {loading && <LoadingSpinner />}
      <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 40 }} />

      <section style={{
        position: "relative", zIndex: 40, background: "#F8F9FC", borderRadius: 24,
        boxShadow: "0 32px 80px rgba(0,0,0,0.22), 0 0 0 1px rgba(255,255,255,0.08)",
        padding: "20px 28px 28px", marginTop: 32,
        width: isCompanyCheckout ? 1520 : 1350, maxWidth: "98vw",
        display: "flex", flexDirection: "column",
      }}>

        {/* ── Header ── */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          marginBottom: 20, paddingBottom: 15, borderBottom: "1px solid #E8EAF0",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: "linear-gradient(135deg,#1b4597 0%,#2a5ec7 100%)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="1" y="4" width="22" height="16" rx="3" ry="3" /><line x1="1" y1="10" x2="23" y2="10" />
              </svg>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <span style={{ fontSize: 20, fontWeight: 700, color: "#111827", letterSpacing: "-0.01em" }}>
                {t("Payment")}
              </span>

              {getCustomerState?.mobile && (
                <div style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "5px 14px", borderRadius: 20,
                  background: "#EEF3FF", border: "1px solid #C7D6F8",
                }}>
                  <span style={{ fontSize: 17, fontWeight: 600, color: "#1b4597" }}>
                    {getCustomerState?.userName}
                  </span>
                  {getCustomerState?.mobile && (
                    <span style={{
                      fontSize: 16, color: "black",
                      borderLeft: "1px solid #C7D6F8",
                      paddingLeft: 8,
                    }}>
                      {getCustomerState?.mobile}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          <button
            type="button" onClick={onClose}
            style={{
              background: "#F3F4F6", border: "none", width: 36, height: 36, borderRadius: 10,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", color: "#6B7280",
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = "#E5E7EB"}
            onMouseLeave={(e) => e.currentTarget.style.background = "#F3F4F6"}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ── Body ── */}
        <main style={{ display: "flex", gap: 0 }}>

          {/* Company Delivery column */}
          {isCompanyCheckout && (
            <div style={{
              borderInlineEnd: "1px solid #E8EAF0",
              paddingInlineEnd: 24,
              marginInlineEnd: 24,
              minWidth: 200,
            }}>
              <SectionLabel>{t("Company Delivery")}</SectionLabel>
              <div style={{ marginTop: 8 }}>
                {orderFromOptions.map((method) => (
                  <OrderFromButton
                    key={method.id} paymentMethod={method}
                    selected={selectedOrderFrom?.id === method.id}
                    onClick={() => handleOrderFromClick(method)}
                    disabled={grandTotal <= 0}
                  />
                ))}
              </div>
            </div>
          )}

          <div style={{ display: "flex", flex: 1, gap: 28 }}>

            {/* ══════════ LEFT: Summary + Order Discount + Payment Options + Done ══════════ */}
            <div style={{ width: "50%", display: "flex", flexDirection: "column" }}>

              {/* Summary card */}
              <div className="pm-card" style={{ padding: "12px 16px", marginBottom: 10 }}>
                <SectionLabel>{t("Summary")}</SectionLabel>

                {/* Total Bill = original before product-line discounts */}
                {totalBillAmount != grandTotal && (
                  <ValueRow label={t("Total Bill")} value={`EGP ${fmtMoney(totalBillAmount)}`} />
                )}

                {/* Grand Total = after product-line discounts, before order-level discount */}
                <ValueRow
                  label={t("Grand Total")} highlight
                  value={
                    <span>
                      EGP {fmtMoney(grandTotal)}
                      {/* {isETAenabled && (
                        <span style={{ fontSize: 12, color: "#9CA3AF", marginLeft: 8, fontWeight: 400 }}>
                          Tax: EGP {totalTax.toFixed(2)}
                        </span>
                      )} */}
                    </span>
                  }
                />

                {/* Show order-level discount row when applied */}
                {orderDiscountAmount > 0 && (
                  <>
                    <ValueRow
                      label={
                        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          {t("Order Discount")}
                          <span style={{
                            fontSize: 10, fontWeight: 700, color: "#fff",
                            background: "linear-gradient(135deg,#D97706,#B45309)",
                            borderRadius: 6, padding: "2px 6px", letterSpacing: "0.04em",
                          }}>
                            -{orderDiscountPercent.toFixed(1)}%
                          </span>
                        </span>
                      }
                      value={`− EGP ${orderDiscountAmount.toFixed(2)}`}
                    />
                    <ValueRow
                      label={<strong>{t("Net Payable")}</strong>}
                      value={`EGP ${(effectiveTotal).toFixed(2)}`}
                      highlight
                    />
                  </>
                )}

                {/* Cash add-on split */}
                {showCashAddon && parseFloat(cashAmount) > 0 ? (
                  <>
                    <ValueRow
                      label={
                        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          {t("Cash")}
                          <span style={{
                            fontSize: 10, fontWeight: 700, color: "#fff",
                            background: "linear-gradient(135deg,#7EC242,#6BB035)",
                            borderRadius: 6, padding: "2px 6px", letterSpacing: "0.04em",
                          }}>{t("CASH")}</span>
                        </span>
                      }
                      value={`EGP ${parseFloat(cashAmount).toFixed(2)}`}
                    />
                    <ValueRow
                      label={t(selectedPaymentMethod?.name)}
                      value={`EGP ${methodAmount.toFixed(2)}`}
                    />
                  </>
                ) : !showCashAddon ? (
                  <>
                    <ValueRow
                      label={selectedPaymentMethod?.name === "Cash & Bank" ? t("Total paid by cash") : t("Total Paid")}
                      value={
                        grandTotal <= 0 ? "EGP 0.00"
                          : totalPaid ? `EGP ${parseFloat(totalPaid).toFixed(2)}`
                            : "EGP —"
                      }
                    />
                    {grandTotal > 0 && (
                      <ValueRow
                        label={selectedPaymentMethod?.name === "Cash & Bank" ? t("Total paid by bank") : t("Change")}
                        value={
                          selectedPaymentMethod?.name === "Cash & Bank"
                            ? `EGP ${remainingAmount.toFixed(2)}`
                            : grandTotal < 0
                              ? `EGP ${grandTotal.toFixed(2)}`
                              : `EGP ${Number(change).toFixed(2)}`
                        }
                      />
                    )}
                  </>
                ) : null}

                {creditAmount?.response && (
                  <ValueRow label={t("Balance Credit Amount")} value={`EGP ${creditAmount.response}`} />
                )}
                {showReturnDelivery && (
                  <ValueRow label={t("Refund Delivery Fee")} value={`EGP ${parseFloat(returnDeliveryFee || 0).toFixed(2)}`} />
                )}
                {(selectedOrderFrom?.name === "self_delivery" || selectedPaymentMethod?.name === "COD") && (
                  <ValueRow label={t("Delivery Fee")} value={`EGP ${parseFloat(deliveryFee || 0).toFixed(2)}`} />
                )}
              </div>



              {/* Payment options */}
              <div className="pm-card" style={{ padding: "16px", marginBottom: 16 }}>
                <SectionLabel>{t("Payment Options")}</SectionLabel>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                  {paymentMethods?.map((method, index) => (
                    <PaymentButton
                      key={method.id} paymentMethod={method}
                      selected={selectedPaymentMethod?.name === method.name || activePaymentIndex === index}
                      onClick={() => handlePaymentMethodClick(method, selectedOrderFrom)}
                    />
                  ))}
                </div>
              </div>

              <div style={{ flex: 1 }} />

              {/* Done button */}
              <button
                className="pm-done-btn" disabled={isDoneDisabled} onClick={handleCheckoutClick}
                style={{
                  width: "calc(100% - 10px)", padding: "17px 0",
                  fontSize: 22, fontWeight: 700, letterSpacing: "0.02em", color: "#fff",
                  background: isDoneDisabled ? "#9CA3AF" : "#1b4597",
                  border: "none", borderRadius: 14, cursor: isDoneDisabled ? "not-allowed" : "pointer",
                  boxShadow: isDoneDisabled ? "none" : "0 8px 24px rgba(27,69,151,0.35)",
                }}
              >
                {t("Done")}
              </button>
            </div>

            {/* ══════════ RIGHT: Input + Keypad ══════════ */}
            <div style={{ width: "50%", display: "flex", flexDirection: "column" }}>
              <div dir={isRtl ? "rtl" : "ltr"} style={{ display: "flex", gap: 24 }}>
                <div style={{ flex: 1 }}>
                  {/* ══ ORDER-LEVEL DISCOUNT CARD ══ */}
                  {allowBillDiscount && totalBillAmount > 0 && (
                    <DiscountCard
                      t={t}
                      discountType={discountType}
                      setDiscountType={setDiscountType}
                      discountValue={discountValue}
                      setDiscountValue={(val) => {
                        setDiscountValue(val);
                        setTotalPaid(""); setTenderCash(0); setChange("");
                      }}
                      maxDiscount={maxDiscount}
                      originalTotal={originalTotal}
                      discountAmount={orderDiscountAmount}
                      discountPercent={orderDiscountPercent}
                      billDiscountAmount={cappedBillDiscount}
                    />
                  )}
                  {showCashAddon ? (
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                        <SectionLabel style={{ margin: 0 }}>{t("Cash Amount")}</SectionLabel>
                        <span style={{
                          fontSize: 11, fontWeight: 700, color: "#7EC242",
                          background: "#F0FAE8", border: "1px solid #C5E8A3",
                          borderRadius: 20, padding: "2px 8px", letterSpacing: "0.04em",
                        }}>
                          {t("Optional")}
                        </span>
                      </div>
                      <div style={{ display: "flex", gap: 10 }}>
                        <div
                          className="pm-card"
                          style={{
                            display: "flex", alignItems: "center", flex: 1, height: 55,
                            padding: "0 14px", gap: 8,
                            border: "2px solid #7EC242", boxShadow: "0 0 0 3px rgba(126,194,66,0.12)",
                            flexDirection: isRtl ? "row-reverse" : "row",
                          }}
                        >
                          <span style={{ color: "#7EC242", fontSize: 14, fontWeight: 700, flexShrink: 0 }}>EGP</span>
                          <input
                            type="number" className="pm-input-no-arrows" autoFocus
                            onFocus={(e) => { if (deviceType === "tablet") e.target.blur(); }}
                            style={{
                              flex: 1, border: "none", outline: "none", background: "transparent",
                              fontSize: 22, fontWeight: 700, color: "#1F2937",
                              direction: "ltr", textAlign: "left",
                            }}
                            placeholder="0.00" value={cashAmount}
                            onChange={(e) => {
                              const sanitized = e.target.value.replace(/ز/g, ".").replace(/[^0-9.]/g, "");
                              if (/^\d*\.?\d{0,2}$/.test(sanitized)) {
                                const gt = parseFloat(String(grandTotal).replace(/[^0-9.]/g, "")) || 0;
                                if ((parseFloat(sanitized) || 0) <= gt) { setCashAmount(sanitized); setTenderCash(sanitized); }
                              }
                            }}
                            maxLength={7}
                          />
                        </div>
                        <button
                          className={`pm-clear-btn${isClearDisabled ? " pm-clear-btn--disabled" : ""}`}
                          onClick={isClearDisabled ? null : handleClearButtonClick}
                          style={{
                            height: 55, padding: "0 20px",
                            background: isClearDisabled ? "#F3F4F6" : "linear-gradient(135deg,#F42020 0%,#D91A1A 100%)",
                            color: isClearDisabled ? "#9CA3AF" : "#fff",
                            border: "none", borderRadius: 12, fontSize: 15, fontWeight: 700,
                            cursor: isClearDisabled ? "not-allowed" : "pointer", flexShrink: 0,
                            boxShadow: isClearDisabled ? "none" : "0 4px 14px rgba(244,32,32,0.30)",
                          }}
                        >
                          {t("Clear")}
                        </button>
                      </div>
                      <div style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        marginTop: 10, padding: "10px 16px",
                        background: "linear-gradient(135deg,#EEF3FF,#F5F8FF)",
                        borderRadius: 12, border: "1px solid #C7D6F8",
                      }}>
                        <span style={{ fontSize: 16, color: "#6B7280", fontWeight: 500 }}>
                          {t(selectedPaymentMethod?.name)} {t("amount")}
                        </span>
                        <span style={{ fontSize: 20, fontWeight: 700, color: "#1b4597", }}>
                          EGP {methodAmount.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div style={{ marginBottom: 14 }}>
                      <SectionLabel>
                        {selectedPaymentMethod?.name === "Cash & Bank" ? t("Total paid by cash") : t("Total Paid")}
                      </SectionLabel>
                      <div style={{ display: "flex", gap: 10 }}>
                        <div
                          className="pm-card"
                          style={{
                            display: "flex", alignItems: "center", flex: 1, height: 60,
                            padding: "0 14px", gap: 8,
                            border: "2px solid #1b4597", boxShadow: "0 0 0 3px rgba(27,69,151,0.10)",
                            flexDirection: isRtl ? "row-reverse" : "row",
                          }}
                        >
                          <span style={{ color: "#1b4597", fontSize: 14, fontWeight: 700, flexShrink: 0 }}>EGP</span>
                          <input
                            id="textInput1" ref={totalPaidInputRef}
                            type="number" className="pm-input-no-arrows" autoFocus
                            onFocus={(e) => { if (deviceType === "tablet") e.target.blur(); }}
                            style={{
                              flex: 1, border: "none", outline: "none", background: "transparent",
                              fontSize: 22, fontWeight: 700, color: "#1F2937",
                              direction: "ltr", textAlign: "left",
                            }}
                            value={grandTotal <= 0 ? "0" : totalPaid}
                            readOnly={
                              grandTotal < 0 ||
                              selectedPaymentMethod?.name === "COD" ||
                              selectedPaymentMethod?.name === "Credit"
                            }
                            onChange={(e) => {
                              const sanitized = e.target.value.replace(/ز/g, ".").replace(/[^0-9.]/g, "");
                              if (/^\d*\.?\d{0,2}$/.test(sanitized)) {
                                setTotalPaid(sanitized); setTenderCash(sanitized || 0);
                                const num = parseFloat(sanitized) || 0;
                                // ── Use effectiveTotal (grandTotal − orderDiscount) ──
                                setChange(`${Math.max(num - effectiveTotal, 0)}`);
                                if (selectedPaymentMethod?.name === "Cash & Bank")
                                  setRemainingAmount(Math.max(effectiveTotal - num, 0));
                              }
                            }}
                            maxLength={7}
                          />
                        </div>
                        <button
                          className={`pm-clear-btn${isClearDisabled ? " pm-clear-btn--disabled" : ""}`}
                          onClick={isClearDisabled ? null : handleClearButtonClick}
                          style={{
                            height: 60, padding: "0 20px",
                            background: isClearDisabled ? "#F3F4F6" : "linear-gradient(135deg,#F42020 0%,#D91A1A 100%)",
                            color: isClearDisabled ? "#9CA3AF" : "#fff",
                            border: "none", borderRadius: 12, fontSize: 15, fontWeight: 700,
                            cursor: isClearDisabled ? "not-allowed" : "pointer", flexShrink: 0,
                            boxShadow: isClearDisabled ? "none" : "0 4px 14px rgba(244,32,32,0.30)",
                          }}
                        >
                          {t("Clear")}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Return delivery fee */}
                  {showReturnDelivery && (
                    <div style={{ marginBottom: 14 }}>
                      <SectionLabel>{t("Refund Delivery Fee")}</SectionLabel>
                      <div
                        className="pm-card"
                        style={{
                          display: "flex", alignItems: "center", height: 52, padding: "0 14px", gap: 8,
                          border: errorMessage ? "2px solid #F42020" : "1px solid #EBEBF0",
                          flexDirection: isRtl ? "row-reverse" : "row",
                        }}
                      >
                        <span style={{ color: "#6B7280", fontSize: 14, fontWeight: 600 }}>EGP</span>
                        <input
                          id="textInput2" className="pm-input-no-arrows"
                          value={returnDeliveryFee}
                          style={{
                            flex: 1, border: "none", outline: "none", background: "transparent",
                            fontSize: 20, fontWeight: 700, color: "#1F2937",
                            direction: "ltr", textAlign: "left",
                          }}
                          onChange={(e) => {
                            const sanitized = e.target.value.replace(/ز/g, ".").replace(/[^0-9.]/g, "");
                            if (/^\d*\.?\d{0,2}$/.test(sanitized)) {
                              let val = sanitized.startsWith(".") ? "0" + sanitized : sanitized;
                              val = val.replace(/^0+(?=\d)/, "") || "0";
                              setErrorMessage(parseFloat(val) > constReturnDeliveryFee
                                ? `Value cannot exceed ${constReturnDeliveryFee}` : "");
                              setReturnDeliveryFee(val);
                            }
                          }}
                          onBlur={() => { if (returnDeliveryFee === 0) setReturnDeliveryFee(0); }}
                          maxLength={7}
                        />
                      </div>
                      {errorMessage && <p style={{ color: "#F42020", fontSize: 12, marginTop: 4 }}>{errorMessage}</p>}
                    </div>
                  )}

                  {/* Delivery fee */}
                  {(selectedOrderFrom?.name === "self_delivery" || selectedPaymentMethod?.name === "COD") && (
                    <div style={{ marginBottom: 14 }}>
                      <SectionLabel>{t("Delivery Fee")}</SectionLabel>
                      <div className="pm-card" style={{
                        display: "flex", alignItems: "center", height: 52, padding: "0 14px", gap: 8,
                        flexDirection: isRtl ? "row-reverse" : "row",
                      }}>
                        <span style={{ color: "#6B7280", fontSize: 14, fontWeight: 600 }}>EGP</span>
                        <input
                          id="textInput3" type="number" className="pm-input-no-arrows" autoFocus
                          value={deliveryFee}
                          style={{
                            flex: 1, border: "none", outline: "none", background: "transparent",
                            fontSize: 20, fontWeight: 700, color: "#1F2937",
                            direction: "ltr", textAlign: "left",
                          }}
                          onFocus={(e) => { if (deviceType === "tablet") e.target.blur(); }}
                          onChange={(e) => {
                            const sanitized = e.target.value.replace(/ز/g, ".").replace(/[^0-9.]/g, "");
                            if (/^\d*\.?\d{0,2}$/.test(sanitized)) setDeliveryFee(sanitized);
                          }}
                          maxLength={7}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Salesman */}
            
              <div className="flex gap-4 mb-4">
                  {showSalesmanSelection && 
                <div className="flex-1">
                  <div className="-mb-1">
                    <SectionLabel>{t("Salesman")}</SectionLabel>
                  </div>

                  <SelectBox
                    width="w-full"
                    name="salesman"
                    defaultValue={null}
                    onChange={(e) => setSelectedSalesman(e.target)}
                    value={selectedSalesman}
                    options={[
                      { value: null, label: t("Select"), disabled: true },
                      ...allSalesman?.map((item) => ({
                        value: item.id,
                        label: `${item.name} (${item?.phone_no})`,
                      })),
                    ]}
                  />
                </div>
              }
                {showDeliveryBoySelection && isCompanyCheckout &&  selectedOrderFrom?.name === "self_delivery" && (
<div className="flex-1">
                  <div className="-mb-1">
                    <SectionLabel>{t("Delivery Boy")}</SectionLabel>
                  </div>

                  <SelectBox
                    width="w-full"
                    name="delivery boys"
                    defaultValue={null}
                    onChange={(e) => setSelectedDeliveryBoys(e.target)}
                    value={selectedDeliveryBoys}
                    options={[
                      { value: null, label: t("Select"), disabled: true },
                      ...allDeliveryBoys?.map((item) => ({
                        value: item.id,
                        label: `${item.name} (${item?.phone_no})`,
                      })),
                    ]}
                  />
                </div>
                )}
                
              </div>



              {/* ── Keypad ── */}
              <div style={{ direction: "ltr" }}>
                {showCashAddon && (
                  <div style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    marginBottom: 8, padding: "4px 10px",
                    background: "#F0FAE8", borderRadius: 8, border: "1px solid #C5E8A3",
                  }}>
                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#7EC242" }} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: "#5A9E2F" }}>
                      {t("Keypad → Cash Amount")}
                    </span>
                  </div>
                )}

                {keypadValues.map((row, rowIndex) => (
                  <div key={rowIndex} style={{ display: "flex", marginBottom: 10 }}>
                    {row.map((item, index) => (
                      <KeypadButton
                        key={index} value={item.value}
                        disabled={isKeypadDisabled}
                        onClick={() => handleKeypadButtonClick(item.value)}
                      >
                        {item.value}
                      </KeypadButton>
                    ))}
                  </div>
                ))}
              </div>

            </div>
          </div>
        </main>
        <footer />
      </section>
    </div>
  );
}

export default Payment_Modal;

// import * as React from "react";
// import { useState, useEffect } from "react";
// import axiosInstance from "../../AxiosInstance";
// import { URLS } from "../../API/API endpoints";
// import { useSelector, useDispatch } from "react-redux";
// import { createOrder } from "../../Redux/Action/createposorderAction";
// import { useTranslation } from "react-i18next";
// import { storeSelectedPaymentMethod } from "../../Redux/Action/paymentAction";
// import { toast } from "react-toastify";
// import i18next from "i18next";
// import { detectWebDeviceType } from "../../utils";
// import SelectBox from "../CustomWidgets/SelectBox";

// /* ─────────────────────────────────────────────
//    Inline styles injected once
// ───────────────────────────────────────────── */
// const GLOBAL_STYLES = `
//   @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap');

//   .pm-modal * { box-sizing: border-box; }
//   .pm-modal { font-family: 'DM Sans', sans-serif; }

//   .pm-spinner { display: flex; gap: 8px; }
//   .pm-spinner span {
//     width: 12px; height: 12px; background: #fff; border-radius: 50%;
//     animation: pm-bounce 0.9s ease-in-out infinite;
//   }
//   .pm-spinner span:nth-child(2) { animation-delay: 0.15s; }
//   .pm-spinner span:nth-child(3) { animation-delay: 0.30s; }
//   @keyframes pm-bounce {
//     0%,80%,100% { transform: scale(0.6); opacity: 0.5; }
//     40%          { transform: scale(1);   opacity: 1;   }
//   }

//   .pm-key { transition: transform 0.12s ease, box-shadow 0.12s ease; }
//   .pm-key:not(:disabled):hover  { transform: translateY(-2px); box-shadow: 0 6px 18px rgba(0,0,0,0.12); }
//   .pm-key:not(:disabled):active { transform: translateY(0); box-shadow: none; }

//   .pm-pill { transition: all 0.18s ease; }
//   .pm-pill:hover:not(.pm-pill--disabled) {
//     border-color: #1b4597 !important; color: #1b4597 !important;
//     box-shadow: 0 4px 14px rgba(27,69,151,0.15); transform: translateY(-1px);
//   }

//   .pm-order-btn { transition: all 0.18s ease; }
//   .pm-order-btn:hover { filter: brightness(1.08); transform: translateY(-2px); box-shadow: 0 8px 20px rgba(0,0,0,0.15); }

//   .pm-done-btn { transition: all 0.2s ease; position: relative; overflow: hidden; }
//   .pm-done-btn::after {
//     content: ''; position: absolute; inset: 0;
//     background: linear-gradient(135deg, rgba(255,255,255,0.15) 0%, transparent 60%);
//     pointer-events: none;
//   }
//   .pm-done-btn:not(:disabled):hover {
//     transform: translateY(-2px); box-shadow: 0 10px 30px rgba(27,69,151,0.40); filter: brightness(1.08);
//   }

//   .pm-input-no-arrows::-webkit-inner-spin-button,
//   .pm-input-no-arrows::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
//   .pm-input-no-arrows { -moz-appearance: textfield; }

//   .pm-clear-btn { transition: all 0.18s ease; }
//   .pm-clear-btn:not(.pm-clear-btn--disabled):hover {
//     filter: brightness(1.1); transform: translateY(-1px); box-shadow: 0 6px 18px rgba(244,32,32,0.30);
//   }

//   .pm-card {
//     background: #fff; border-radius: 16px;
//     border: 1px solid #EBEBF0; box-shadow: 0 2px 8px rgba(0,0,0,0.04);
//   }
// `;

// function injectStyles() {
//   if (document.getElementById("pm-global-styles")) return;
//   const tag = document.createElement("style");
//   tag.id = "pm-global-styles";
//   tag.textContent = GLOBAL_STYLES;
//   document.head.appendChild(tag);
// }

// /* ─────────────────────────────────────────────
//    Helper: which methods get the cash add-on.
//    Excludes methods that already handle cash natively.
// ───────────────────────────────────────────── */
// const CASH_ADDON_EXCLUDED = new Set(["Cash", "Cash & Bank", "COD", "Credit"]);
// const supportsCashAddon = (methodName) =>
//   Boolean(methodName && !CASH_ADDON_EXCLUDED.has(methodName));

// /* ─────────────────────────────────────────────
//    Sub-components
// ───────────────────────────────────────────── */
// const LoadingSpinner = () => (
//   <div style={{ zIndex: 50 }} className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black bg-opacity-50">
//     <div className="pm-spinner"><span /><span /><span /></div>
//   </div>
// );

// function SectionLabel({ children }) {
//   return (
//     <p style={{ fontSize: 14, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "#494949", marginBottom: 3 }}>
//       {children}
//     </p>
//   );
// }

// function ValueRow({ label, value, highlight }) {
//   return (
//     <div style={{
//       display: "flex", alignItems: "center", justifyContent: "space-between",
//       padding: "10px 16px", borderRadius: 10, marginBottom: 8,
//       background: highlight ? "linear-gradient(135deg,#EEF3FF 0%,#F5F8FF 100%)" : "#FAFAFA",
//       border: highlight ? "1px solid #C7D6F8" : "1px solid #F0F0F0",
//     }}>
//       <span style={{ fontSize: 16, color: "#494949", fontWeight: 500 }}>{label}</span>
//       <span style={{ fontSize: 20, fontWeight: 700, color: highlight ? "#1b4597" : "#181818", fontFamily: "'DM Mono', monospace", direction: "ltr" }}>
//         {value}
//       </span>
//     </div>
//   );
// }

// function OrderFromButton({ paymentMethod, selected, onClick, disabled }) {
//   const { t } = useTranslation();
//   const name = paymentMethod.name === "self_delivery" ? t("Self Delivery") : t(paymentMethod.name);
//   return (
//     <div className="pm-order-btn" onClick={onClick} style={{
//       display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
//       marginTop: 10, padding: "14px 24px", fontSize: 17, fontWeight: 600, borderRadius: 14,
//       border: selected ? "3px solid rgba(255,255,255,0.6)" : "2px solid rgba(255,255,255,0.25)",
//       boxShadow: selected ? "0 0 0 3px rgba(27,69,151,0.30), 0 6px 20px rgba(0,0,0,0.18)" : "0 2px 8px rgba(0,0,0,0.10)",
//       cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
//       backgroundColor: paymentMethod?.background_color || "#7EC242",
//       color: paymentMethod?.text_color || "#fff", whiteSpace: "nowrap",
//     }}>
//       {name}
//     </div>
//   );
// }

// function PaymentButton({ paymentMethod, selected, onClick, disabled }) {
//   const { t } = useTranslation();
//   const nameMap = {
//     Cash: t("Cash"), Bank: t("Bank"), COD: t("COD"),
//     "Cash & Bank": t("Cash & Bank"), "Credit Card": t("Credit Card"),
//     InstaPay: t("InstaPay"), Credit: t("Credit"),
//   };
//   return (
//     <div
//       className={`pm-pill${disabled ? " pm-pill--disabled" : ""}`}
//       onClick={!disabled ? onClick : undefined}
//       style={{
//         display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
//         padding: "10px 8px", minHeight: 56, borderRadius: 12, textAlign: "center",
//         border: selected ? "2.5px solid #1b4597" : "1.5px solid #E2E5EB",
//         background: selected ? "linear-gradient(135deg,#EEF3FF 0%,#F0F5FF 100%)" : "#FAFAFA",
//         color: selected ? "#1b4597" : "#494949", fontWeight: selected ? 700 : 500,
//         fontSize: 18, lineHeight: 1.3, cursor: disabled ? "not-allowed" : "pointer",
//         opacity: disabled ? 0.5 : 1,
//         boxShadow: selected ? "0 4px 14px rgba(27,69,151,0.14)" : "none",
//       }}
//     >
//       {nameMap[paymentMethod.name] || t(paymentMethod.name)}
//     </div>
//   );
// }

// function KeypadButton({ value, onClick, children, disabled }) {
//   const isGreen = ["10", "20", "50", "100", "200"].includes(String(value));
//   return (
//     <button
//       type="button" className="pm-key"
//       onClick={disabled ? null : onClick}
//       disabled={disabled}
//       style={{
//         display: "flex", alignItems: "center", justifyContent: "center",
//         height: 64, width: 148, marginInlineEnd: 10, borderRadius: 12,
//         fontSize: 22, fontWeight: 600, fontFamily: "'DM Mono', monospace",
//         border: isGreen ? "none" : "1px solid #E2E5EB",
//         background: isGreen
//           ? "linear-gradient(135deg,#7EC242 0%,#6BB035 100%)"
//           : "linear-gradient(180deg,#FFFFFF 0%,#F5F5F5 100%)",
//         color: isGreen ? "#fff" : "#1F2937",
//         boxShadow: isGreen ? "0 4px 14px rgba(126,194,66,0.35)" : "0 2px 6px rgba(0,0,0,0.07)",
//         cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.4 : 1,
//         letterSpacing: "0.02em",
//       }}
//     >
//       {children || value}
//     </button>
//   );
// }

// /* ─────────────────────────────────────────────
//    Main Payment_Modal
// ───────────────────────────────────────────── */
// function Payment_Modal({
//   onClose, isCompanyCheckout, grandTotal, handleCheckout,
//   setTenderCash, creditAmount, setCreditAmount,
//   deliveryCheck, deliveryFee, setDeliveryFee,
//   returnDeliveryFee, setReturnDeliveryFee, showReturnDelivery,
//   constReturnDeliveryFee, discountType, setDiscountType,
//   discountValue, setDiscountValue, originalTotal,
//   totalBillAmount, totalCostPrice,
// }) {
//   injectStyles();

//   const { t, i18n } = useTranslation();
//   const deviceType = detectWebDeviceType();
//   const { user } = useSelector((state) => state?.login);
//   const dispatch = useDispatch();

//   // ─── existing state ───────────────────────────────────────────────────────
//   const [totalPaid, setTotalPaid]                       = useState("");
//   const [change, setChange]                             = useState("");
//   const [selectedPaymentMethod, setSelectedPaymentMethod] = useState();
//   const [isBankPaymentSelected, setIsBankPaymentSelected] = useState(false);
//   const [paymentMethods, setPaymentMethods]             = useState([]);
//   const [orderFromOptions, setOrderFromOptions]         = useState([]);
//   const [allSalesman, setAllSalesman]                   = useState([]);
//   const [selectedSalesman, setSelectedSalesman]         = useState(null);
//   const [selectedOrderFrom, setSelectedOrderFrom]       = useState();
//   const [loading, setLoading]                           = useState(false);
//   const [errorMessage, setErrorMessage]                 = useState("");
//   const [activePaymentIndex, setActivePaymentIndex]     = useState(0);
//   const [activeOrderFromIndex, setActiveOrderFromIndex] = useState("");
//   const [cashPaid, setCashPaid]                         = useState(0);
//   const [remainingAmount, setRemainingAmount]           = useState(grandTotal);

//   // ─── CASH ADD-ON state ────────────────────────────────────────────────────
//   // cashAmount  : what the keypad fills (the cash portion paid alongside)
//   // methodAmount: grandTotal − cashAmount — auto-computed, sent to selected method
//   const [cashAmount, setCashAmount]     = useState("");
//   const [methodAmount, setMethodAmount] = useState(grandTotal);

//   // Keep methodAmount in sync whenever cashAmount or grandTotal changes
//   useEffect(() => {
//     const cash = parseFloat(cashAmount) || 0;
//     const gt   = parseFloat(String(grandTotal).replace(/[^0-9.]/g, "")) || 0;
//     setMethodAmount(parseFloat(Math.max(gt - cash, 0).toFixed(2)));
//   }, [cashAmount, grandTotal]);

//   // Reset cash add-on whenever payment method changes
//   const resetCashAddon = () => {
//     setCashAmount("");
//     setTenderCash("");
//     setMethodAmount(parseFloat(String(grandTotal).replace(/[^0-9.]/g, "")) || 0);
//   };
//   // ─────────────────────────────────────────────────────────────────────────

//   const isETAenabled     = useSelector((s) => s?.permissionReducer?.permissions?.is_eta_enabled);
//   const returnProducts   = useSelector((s) => s.returnProductReducer.products);
//   const localProducts    = useSelector((s) => s.products.products);
//   const getCustomerState = useSelector((s) => s?.getcustomer?.selectedCustomer);
//   const user_id          = useSelector((s) => s?.login?.user?.result?.user_id);

//   const totalPaidInputRef = React.useRef(null);

//   const calculateTotalTax = (products) =>
//     products.reduce((total, product) => {
//       const price   = Number(product.price_after_discount) || 0;
//       const qty     = Number(product.quantity) || 1;
//       const taxPct  = isETAenabled && product?.vat_details?.tax_id
//         ? Number(product?.vat_details?.tax_rate_percent) || 0 : 0;
//       return total + (price * qty * taxPct) / (100 + taxPct);
//     }, 0);
//   const totalTax = calculateTotalTax(localProducts);

//   /* ── API calls ── */
//   const getPaymentMethod = async (userID) => {
//     try {
//       setLoading(true);
//       const response = await axiosInstance.post(URLS.PAYMENT_METHODS, { user_id: userID });
//       if (response?.data.result.data != null) {
//         const methods = response.data.result.data.map((m) => ({
//           name: m.name, id: m.id, is_cash_bank: m.is_cash_bank,
//           cash_id: m.cash_payment_method_id,
//           bank_id: m.bank_id ?? m.bank_payment_method_id,
//         }));
//         setPaymentMethods([...methods]);
//         let selectedIndex;
//         if (grandTotal <= 0 && returnProducts?.[0]?.payment_method_id) {
//           selectedIndex = methods.findIndex((m) => {
//             const sameId    = m.id === returnProducts?.[0]?.payment_method_id;
//             const hasBankId = returnProducts?.[0]?.bank_id != null && m.bank_id != null;
//             if (sameId && hasBankId) return m.bank_id === returnProducts?.[0]?.bank_id;
//             return sameId;
//           });
//         } else {
//           selectedIndex = methods.findIndex((m) => m.name === "Cash");
//         }
//         if (selectedIndex !== -1) {
//           const sel = methods[selectedIndex];
//           handlePaymentMethodClick(sel, selectedOrderFrom);
//           setSelectedPaymentMethod(sel);
//           setActivePaymentIndex(selectedIndex);
//         }
//       }
//     } catch (err) { console.error(err); setErrorMessage("Failed to fetch payment methods."); }
//     finally { setLoading(false); }
//   };

//   const getOrderFrom = async () => {
//     try {
//       setLoading(true);
//       const response = await axiosInstance.post(URLS.ORDER_FROM, {
//         user_id, lang: i18next.language === "ar" ? "ar_001" : "en_US",
//       });
//       const posOrderFrom = response?.data?.result?.[0]?.pos_order_from;
//       setOrderFromOptions(posOrderFrom);
//       setSelectedOrderFrom(posOrderFrom[0]);
//       setActiveOrderFromIndex(0);
//       getOrderFromMethods(posOrderFrom[0]?.id, posOrderFrom[0]);
//     } catch (err) { console.error(err); setErrorMessage("Failed to fetch order from options."); }
//     finally { setLoading(false); }
//   };

//   const getSalesman = async () => {
//     try {
//       setLoading(true);
//       const response = await axiosInstance.post(URLS.GET_ALL_SALESMAN, {
//         lang: i18next.language === "ar" ? "ar_001" : "en_US",
//         company_id: user?.result?.company_id, search: "", limit: 100, offset: 0,
//       });
//       setAllSalesman(response?.data?.result?.status_code == 200 ? response?.data?.result?.data : []);
//     } catch (err) { console.error(err); }
//     finally { setLoading(false); }
//   };

//   const getOrderFromMethods = async (id, company) => {
//     try {
//       setLoading(true);
//       const response = await axiosInstance.post(URLS.ORDER_FROM_METHOD, { user_id, pos_order_from_id: id });
//       if (response?.data?.result?.status_code == 200) {
//         const data = response?.data?.result?.data;
//         setPaymentMethods(data);
//         const cashIndex = data?.findIndex((m) => m.name === "Cash");
//         if (cashIndex !== -1) {
//           handlePaymentMethodClick(data[cashIndex], company);
//           setSelectedPaymentMethod(data[cashIndex]);
//           setActivePaymentIndex(cashIndex);
//         } else {
//           handlePaymentMethodClick(data[0], company);
//         }
//       } else { setPaymentMethods([]); }
//     } catch (err) { console.error(err); }
//     finally { setLoading(false); }
//   };

//   const getCreditAmount = async () => {
//     try {
//       setLoading(true);
//       const response = await axiosInstance.post(URLS.GET_CUSTOMER_CREDIT, { phone: getCustomerState?.mobile });
//       if (response?.data?.result[0].status_code === 200) setCreditAmount(response?.data?.result[0]);
//     } catch (err) { console.error(err); }
//     finally { setLoading(false); }
//   };

//   useEffect(() => { if (isCompanyCheckout) getOrderFrom(); }, [user?.result?.user_id]);
//   useEffect(() => {
//     if (!deliveryCheck && !isCompanyCheckout) getPaymentMethod(user?.result?.user_id);
//     getSalesman();
//     if (getCustomerState) getCreditAmount();
//   }, [user?.result?.user_id, getCustomerState]);

//   useEffect(() => {
//     if (grandTotal < 0) { setTotalPaid(grandTotal.toString()); setIsBankPaymentSelected(true); }
//   }, [grandTotal]);

//   /* ── Keypad layout ── */
//   const keypadValues = [
//     [{ value: "1" }, { value: "2" }, { value: "3" }, { value: "10"  }],
//     [{ value: "4" }, { value: "5" }, { value: "6" }, { value: "20"  }],
//     [{ value: "7" }, { value: "8" }, { value: "9" }, { value: "50"  }],
//     [{ value: "." }, { value: "0" }, { value: "200" }, { value: "100" }],
//   ];

//   /* ─────────────────────────────────────────────────────────────────────────
//      KEYPAD HANDLER
//      · Methods with cash add-on (Bank, InstaPay, etc.):
//          Keypad ALWAYS fills cashAmount. methodAmount auto-updates.
//      · Cash / Cash & Bank / COD / Credit:
//          Keypad fills totalPaid as before (original behaviour).
//   ───────────────────────────────────────────────────────────────────────── */
//   const handleKeypadButtonClick = (value) => {
//     const usesCashAddon = supportsCashAddon(selectedPaymentMethod?.name);

//     if (usesCashAddon) {
//       /* ── Cash add-on path ─────────────────────────────────────────────── */
//       const gt = parseFloat(String(grandTotal).replace(/[^0-9.]/g, "")) || 0;

//       if (value === "C") {
//         setCashAmount("");
//     setTenderCash("");
//         return;
//       }
//       if (value === ".") {
//         if (!cashAmount.includes(".")){
//           setCashAmount((p) => p + ".");
//           setCashAmount((p) => p + ".");
//         } 
//         return;
//       }
//       if (!isNaN(parseInt(value, 10))) {
//         const isQuickAdd = ["10", "20", "50", "100", "200"].includes(value);
//         if (isQuickAdd) {
//           // Add to existing amount, capped at grandTotal
//           const upd = Math.min(parseFloat(cashAmount || 0) + parseFloat(value), gt);
//           setCashAmount(upd.toString());
//     setTenderCash(upd.toString());
//         } else {
//           if (cashAmount.length >= 10) return;
//           const upd = cashAmount + value;
//           if ((parseFloat(upd) || 0) <= gt){ 
//             setCashAmount(upd)
//             setTenderCash(upd)
//           };
//         }
//       }
//       setErrorMessage("");
//       return;
//     }

//     /* ── Original totalPaid path ────────────────────────────────────────── */
//     if (
//       totalPaid.length >= 10 &&
//       value !== "C" && value !== "." &&
//       !["50", "100", "200"].includes(value)
//     ) return;

//     if (value === "C") {
//       setTotalPaid(""); setTenderCash(0); setChange("");
//     } else if (value === ".") {
//       if (!totalPaid.includes(".")) {
//         setTotalPaid((p) => p + ".");
//         setTenderCash((p) => String(p) + "." || 0);
//       }
//     } else if (!isNaN(parseInt(value, 10))) {
//       const gt = parseFloat(String(grandTotal).replace(/[^0-9.]/g, "")) || 0;
//       if (["50", "100", "200", "20", "10"].includes(value)) {
//         const upd = parseFloat(totalPaid || 0) + parseFloat(value);
//         setTotalPaid(upd.toString()); setTenderCash(upd.toString() || 0);
//         setChange(`${Math.max(upd - gt, 0)}`);
//       } else {
//         const upd = totalPaid + value;
//         setTotalPaid(upd); setTenderCash(upd || 0);
//         const num = parseFloat(upd.replace(/[^0-9.]/g, "")) || 0;
//         setChange(`${Math.max(num - gt, 0)}`);
//         if (selectedPaymentMethod?.name === "Cash & Bank")
//           setRemainingAmount(Math.max(gt - num, 0));
//       }
//     }
//     setErrorMessage("");
//   };

//   /* ── Payment method selection ── */
//   const handlePaymentMethodClick = (method, company) => {
//     // Always reset cash add-on when switching methods
//     resetCashAddon();

//     if (grandTotal <= 0) setTotalPaid(0);
//     if (company?.name === "self_delivery") {
//       setDeliveryFee(company?.delivery_fee ?? 0);
//     } else if (method.name === "COD") {
//       setDeliveryFee(selectedOrderFrom?.delivery_fee ?? 0);
//     } else {
//       setDeliveryFee(null);
//     }

//     dispatch(storeSelectedPaymentMethod(method));
//     setSelectedPaymentMethod(method);
//     setActivePaymentIndex(paymentMethods.indexOf(method));

//     if (method.name === "Cash & Bank") {
//       const cashMethod = paymentMethods?.find((m) => m.name === "Cash");
//       const bankMethod = paymentMethods?.find((m) => m.name === "Bank" || m.bank_id);
//       if (cashMethod && bankMethod) {
//         method = { name: method.name, id: { cashId: cashMethod.id, bankId: bankMethod.id } };
//         setSelectedPaymentMethod(method);
//       }
//       setIsBankPaymentSelected(false); setCashPaid(0); setRemainingAmount(grandTotal);
//       setTotalPaid(""); setChange("");

//     } else if (supportsCashAddon(method.name)) {
//       // Bank / InstaPay / Credit Card / Fawry / etc.
//       // NOT locking the keypad — cashAmount drives input now.
//       // totalPaid mirrors grandTotal so the summary is correct before any cash is entered.
//       setIsBankPaymentSelected(false);
//       setTotalPaid(String(grandTotal));
//       setChange("0");

//     } else if (method.name === "COD") {
//       const cashMethod = paymentMethods.find((m) => m.name === "Cash");
//       if (cashMethod) { method = { name: method.name, id: cashMethod.id }; setSelectedPaymentMethod(method); }
//       setIsBankPaymentSelected(false); setTotalPaid(0); setTenderCash(0); setChange("0");

//     } else if (method.name === "Credit") {
//       setIsBankPaymentSelected(false); setTotalPaid(0); setTenderCash(0); setChange("0");

//     } else {
//       // Pure Cash and any unknown method
//       setIsBankPaymentSelected(false); setTenderCash(0); setTotalPaid(""); setChange("");
//     }
//     setErrorMessage("");
//   };

//   const handleOrderFromClick = (method) => {
//     if (selectedOrderFrom?.id === method?.id) return;
//     getOrderFromMethods(method?.id, method);
//     setSelectedOrderFrom(method);
//     setActiveOrderFromIndex(orderFromOptions.indexOf(method));
//   };

//   /* ─────────────────────────────────────────────────────────────────────────
//      buildPaymentMethodForCheckout
//      · No cash add-on → return selectedPaymentMethod unchanged.
//      · Cash add-on active → return enriched object with combined id
//        (same shape as Cash & Bank so the existing backend logic reuses it).
//   ───────────────────────────────────────────────────────────────────────── */
//   const buildPaymentMethodForCheckout = () => {
//     const hasCashAddon =
//       supportsCashAddon(selectedPaymentMethod?.name) &&
//       parseFloat(cashAmount) > 0;

//     if (!hasCashAddon) return selectedPaymentMethod;

//     const cashMethod = paymentMethods.find((m) => m.name === "Cash");

//     return {
//       ...selectedPaymentMethod,
//       // Combined id — mirrors Cash & Bank shape your backend already handles
//       id: {
//         cashId:   cashMethod?.id,
//         methodId: selectedPaymentMethod?.id,
//       },
//       cashAmount:   parseFloat(cashAmount),
//       methodAmount: methodAmount,
//     };
//   };

//   const handleCheckoutClick = () => {
//     if (errorMessage) {
//       toast.error(t(`The Return Delivery Fee cannot exceed EGP ${constReturnDeliveryFee}`));
//       return;
//     }
//     handleCheckout(buildPaymentMethodForCheckout(), selectedOrderFrom, selectedSalesman);
//   };

//   const handleClearButtonClick = () => {
//     setTotalPaid(""); setChange(""); setRemainingAmount(0);
//     setDiscountType(""); setDiscountValue(0);
//     setCashAmount(""); // also clear cash add-on
//     setTenderCash("");
//   };

//   /* ── Keyboard shortcuts ── */
//   const handleKeyPress = (event) => {
//     const normalizedKey = { x: "ء" }[event.key] || event.key;
//     if (event.key === "Enter") {
//       event.preventDefault();
//       if (selectedPaymentMethod?.name === "Cash & Bank" && totalPaid >= grandTotal) return;
//       if (
//         (selectedPaymentMethod?.name === "Cash & Bank" && totalPaid > 0) ||
//         (selectedPaymentMethod?.name === "Cash" && totalPaid >= grandTotal) ||
//         (selectedPaymentMethod?.name === "Bank" && totalPaid == grandTotal) ||
//         selectedPaymentMethod?.name === "COD" ||
//         selectedPaymentMethod?.name === "Credit" ||
//         supportsCashAddon(selectedPaymentMethod?.name)
//       ) {
//         handleCheckoutClick();
//       }
//     } else if (event.shiftKey && normalizedKey === "Shift") {
//       event.preventDefault();
//       if (grandTotal <= 0) return;
//       const nextIndex = (activePaymentIndex + 1) % paymentMethods.length;
//       setActivePaymentIndex(nextIndex);
//       setSelectedPaymentMethod(paymentMethods[nextIndex]);
//       handlePaymentMethodClick(paymentMethods[nextIndex], selectedOrderFrom);
//     } else if (event.ctrlKey && normalizedKey === "ء") {
//       event.preventDefault();
//       handleClearButtonClick();
//     }
//   };

//   useEffect(() => {
//     window.addEventListener("keydown", handleKeyPress);
//     return () => window.removeEventListener("keydown", handleKeyPress);
//   }, [totalPaid, cashAmount, grandTotal, selectedPaymentMethod, activePaymentIndex]);

//   useEffect(() => {
//     if (selectedPaymentMethod?.name === "Cash" || selectedPaymentMethod?.name === "Cash & Bank") {
//       totalPaidInputRef.current?.focus();
//     }
//   }, [selectedPaymentMethod]);

//   /* ── Helpers ── */
//   const isRtl = i18n.language === "ar";
//   const fmtMoney = (val) => {
//     if (isRtl && val < 0) return `${(-val).toFixed(2)}-`;
//     return parseFloat(val || 0).toFixed(2);
//   };

//   // Whether to show the cash add-on UI
//   const showCashAddon = supportsCashAddon(selectedPaymentMethod?.name) && grandTotal > 0;

//   // Keypad disabled only for truly input-free flows
//   const isKeypadDisabled =
//     grandTotal < 0 ||
//     selectedPaymentMethod?.name === "COD" ||
//     selectedPaymentMethod?.name === "Credit";

//   const isClearDisabled =
//     selectedPaymentMethod?.name === "Credit" ||
//     selectedPaymentMethod?.name === "COD" ||
//     grandTotal < 0;

//   const isDoneDisabled =
//     (selectedPaymentMethod?.name === "Cash & Bank" && totalPaid <= 0) ||
//     (selectedPaymentMethod?.name === "Cash" && totalPaid < grandTotal) ||
//     (selectedPaymentMethod?.name === "Cash & Bank" && totalPaid >= grandTotal) ||
//     methodAmount <= 0;

//   /* ─────────────────────────────────────────────
//      RENDER
//   ───────────────────────────────────────────── */
//   return (
//     <div
//       className="pm-modal"
//       style={{
//         position: "fixed", top: 20, left: 0, width: "100%", height: "100%",
//         display: "flex", alignItems: "center", justifyContent: "center",
//         zIndex: 50, backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
//       }}
//     >
//       {loading && <LoadingSpinner />}
//       <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 40 }} />

//       <section style={{
//         position: "relative", zIndex: 40, background: "#F8F9FC", borderRadius: 24,
//         boxShadow: "0 32px 80px rgba(0,0,0,0.22), 0 0 0 1px rgba(255,255,255,0.08)",
//         padding: "20px 28px 28px", marginTop: 32,
//         width: isCompanyCheckout ? 1520 : 1350, maxWidth: "98vw",
//         display: "flex", flexDirection: "column",
//       }}>

//         {/* ── Header ── */}
//         <div style={{
//           display: "flex", alignItems: "center", justifyContent: "space-between",
//           marginBottom: 20, paddingBottom: 15, borderBottom: "1px solid #E8EAF0",
//         }}>
//           <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
//             <div style={{
//               width: 36, height: 36, borderRadius: 10,
//               background: "linear-gradient(135deg,#1b4597 0%,#2a5ec7 100%)",
//               display: "flex", alignItems: "center", justifyContent: "center",
//             }}>
//               <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
//                 <rect x="1" y="4" width="22" height="16" rx="3" ry="3"/><line x1="1" y1="10" x2="23" y2="10"/>
//               </svg>
//             </div>
//             <span style={{ fontSize: 20, fontWeight: 700, color: "#111827", letterSpacing: "-0.01em" }}>
//               {t("Payment")}
//             </span>
//           </div>
//           <button
//             type="button" onClick={onClose}
//             style={{
//               background: "#F3F4F6", border: "none", width: 36, height: 36, borderRadius: 10,
//               display: "flex", alignItems: "center", justifyContent: "center",
//               cursor: "pointer", color: "#6B7280",
//             }}
//             onMouseEnter={(e) => e.currentTarget.style.background = "#E5E7EB"}
//             onMouseLeave={(e) => e.currentTarget.style.background = "#F3F4F6"}
//           >
//             <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M6 18L18 6M6 6l12 12" />
//             </svg>
//           </button>
//         </div>

//         {/* ── Body ── */}
//         <main style={{ display: "flex", gap: 0 }}>

//           {/* Company Delivery column */}
//           {isCompanyCheckout && (
// <div style={{ 
//   borderInlineEnd: "1px solid #E8EAF0",
//   paddingInlineEnd: 24,
//   marginInlineEnd: 24,
//   minWidth: 200 
// }}>              <SectionLabel>{t("Company Delivery")}</SectionLabel>
//               <div style={{ marginTop: 8 }}>
//                 {orderFromOptions.map((method) => (
//                   <OrderFromButton
//                     key={method.id} paymentMethod={method}
//                     selected={selectedOrderFrom?.id === method.id}
//                     onClick={() => handleOrderFromClick(method)}
//                     disabled={grandTotal <= 0}
//                   />
//                 ))}
//               </div>
//             </div>
//           )}

//           <div style={{ display: "flex", flex: 1, gap: 28 }}>

//             {/* ══════════ LEFT: Summary + Payment Options + Done ══════════ */}
//             <div style={{ width: "50%", display: "flex", flexDirection: "column" }}>

//               {/* Summary card */}
//               <div className="pm-card" style={{ padding: "12px 16px", marginBottom: 10 }}>
//                 <SectionLabel>{t("Summary")}</SectionLabel>

//                 {totalBillAmount != grandTotal && (
//                   <ValueRow label={t("Total Bill")} value={`EGP ${fmtMoney(totalBillAmount)}`} />
//                 )}

//                 <ValueRow
//                   label={t("Grand Total")} highlight
//                   value={
//                     <span>
//                       EGP {fmtMoney(grandTotal)}
//                       {isETAenabled && (
//                         <span style={{ fontSize: 12, color: "#9CA3AF", marginLeft: 8, fontWeight: 400 }}>
//                           Tax: EGP {totalTax.toFixed(2)}
//                         </span>
//                       )}
//                     </span>
//                   }
//                 />

//                 {/* Cash add-on: show split when cashAmount > 0 */}
//                 {showCashAddon && parseFloat(cashAmount) > 0 ? (
//                   <>
//                     <ValueRow
//                       label={
//                         <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
//                           {t("Cash")}
//                           <span style={{
//                             fontSize: 10, fontWeight: 700, color: "#fff",
//                             background: "linear-gradient(135deg,#7EC242,#6BB035)",
//                             borderRadius: 6, padding: "2px 6px", letterSpacing: "0.04em",
//                           }}>{t("CASH")}</span>
//                         </span>
//                       }
//                       value={`EGP ${parseFloat(cashAmount).toFixed(2)}`}
//                     />
//                     <ValueRow
//                       label={t(selectedPaymentMethod?.name)}
//                       value={`EGP ${methodAmount.toFixed(2)}`}
//                     />
//                   </>
//                 ) : !showCashAddon && (
//                   /* Standard rows for Cash / Cash&Bank / COD / Credit */
//                   <>
//                     <ValueRow
//                       label={selectedPaymentMethod?.name === "Cash & Bank" ? t("Total paid by cash") : t("Total Paid")}
//                       value={
//                         grandTotal <= 0 ? "EGP 0.00"
//                         : totalPaid ? `EGP ${parseFloat(totalPaid).toFixed(2)}`
//                         : "EGP —"
//                       }
//                     />
//                     {grandTotal > 0 && (
//                       <ValueRow
//                         label={selectedPaymentMethod?.name === "Cash & Bank" ? t("Total paid by bank") : t("Change")}
//                         value={
//                           selectedPaymentMethod?.name === "Cash & Bank"
//                             ? `EGP ${remainingAmount.toFixed(2)}`
//                             : grandTotal < 0
//                               ? `EGP ${grandTotal.toFixed(2)}`
//                               : `EGP ${Number(change).toFixed(2)}`
//                         }
//                       />
//                     )}
//                   </>
//                 )}

//                 {creditAmount?.response && (
//                   <ValueRow label={t("Balance Credit Amount")} value={`EGP ${creditAmount.response}`} />
//                 )}
//                 {showReturnDelivery && (
//                   <ValueRow label={t("Refund Delivery Fee")} value={`EGP ${parseFloat(returnDeliveryFee || 0).toFixed(2)}`} />
//                 )}
//                 {(selectedOrderFrom?.name === "self_delivery" || selectedPaymentMethod?.name === "COD") && (
//                   <ValueRow label={t("Delivery Fee")} value={`EGP ${parseFloat(deliveryFee || 0).toFixed(2)}`} />
//                 )}
//               </div>

//               {/* Payment options */}
//               <div className="pm-card" style={{ padding: "16px", marginBottom: 16 }}>
//                 <SectionLabel>{t("Payment Options")}</SectionLabel>
//                 <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
//                   {paymentMethods?.map((method, index) => (
//                     <PaymentButton
//                       key={method.id} paymentMethod={method}
//                       selected={selectedPaymentMethod?.name === method.name || activePaymentIndex === index}
//                       onClick={() => handlePaymentMethodClick(method, selectedOrderFrom)}
//                     />
//                   ))}
//                 </div>
//               </div>

//               <div style={{ flex: 1 }} />

//               {/* Done button */}
//               <button
//                 className="pm-done-btn" disabled={isDoneDisabled} onClick={handleCheckoutClick}
//                 style={{
//                   width: "calc(100% - 10px)", padding: "17px 0",
//                   fontSize: 22, fontWeight: 700, letterSpacing: "0.02em", color: "#fff",
//                   background: isDoneDisabled ? "#9CA3AF" : "#1b4597",
//                   border: "none", borderRadius: 14, cursor: isDoneDisabled ? "not-allowed" : "pointer",
//                   boxShadow: isDoneDisabled ? "none" : "0 8px 24px rgba(27,69,151,0.35)",
//                 }}
//               >
//                 {t("Done")}
//               </button>
//             </div>

//             {/* ══════════ RIGHT: Input + Keypad ══════════ */}
//             <div style={{ width: "50%", display: "flex", flexDirection: "column" }}>
//               <div dir={isRtl ? "rtl" : "ltr"} style={{ display: "flex", gap: 24 }}>
//                 <div style={{ flex: 1 }}>

//                   {showCashAddon ? (
//                     /* ────────────────────────────────────────────────────────
//                        CASH ADD-ON INPUT
//                        Shown for Bank / InstaPay / Credit Card / etc.
//                        The keypad always feeds into this field.
//                        The method amount pill auto-updates beneath it.
//                     ──────────────────────────────────────────────────────── */
//                     <div style={{ marginBottom: 14 }}>
//                       {/* Label */}
//                       <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5}}>
//                         <SectionLabel style={{ margin: 0 }}>{t("Cash Amount")}</SectionLabel>
//                         <span style={{
//                           fontSize: 11, fontWeight: 700, color: "#7EC242",
//                           background: "#F0FAE8", border: "1px solid #C5E8A3",
//                           borderRadius: 20, padding: "2px 8px", letterSpacing: "0.04em",
//                         }}>
//                           {t("Optional")}
//                         </span>
//                       </div>

//                       {/* Input row */}
//                       <div style={{ display: "flex", gap: 10 }}>
//                         <div
//                           className="pm-card"
//                           style={{
//                             display: "flex", alignItems: "center", flex: 1, height: 55,
//                             padding: "0 14px", gap: 8,
//                             // Green border — always active for this method
//                             border: "2px solid #7EC242",
//                             boxShadow: "0 0 0 3px rgba(126,194,66,0.12)",
//                             flexDirection: isRtl ? "row-reverse" : "row",
//                           }}
//                         >
//                           <span style={{ color: "#7EC242", fontSize: 14, fontWeight: 700, flexShrink: 0 }}>EGP</span>
//                           <input
//                             type="number" className="pm-input-no-arrows"
//                             autoFocus
//                             onFocus={(e) => { if (deviceType === "tablet") e.target.blur(); }}
//                             style={{
//                               flex: 1, border: "none", outline: "none", background: "transparent",
//                               fontSize: 22, fontWeight: 700, color: "#1F2937",
//                               fontFamily: "'DM Mono', monospace", direction: "ltr", textAlign: "left",
//                             }}
//                             placeholder="0.00"
//                             value={cashAmount}
//                             onChange={(e) => {
//                               const sanitized = e.target.value.replace(/ز/g, ".").replace(/[^0-9.]/g, "");
//                               if (/^\d*\.?\d{0,2}$/.test(sanitized)) {
//                                 const gt  = parseFloat(String(grandTotal).replace(/[^0-9.]/g, "")) || 0;
//                                 if ((parseFloat(sanitized) || 0) <= gt) {
//                                   setCashAmount(sanitized)
//                                   setTenderCash(sanitized)
//                                 };
//                               }
//                             }}
//                             maxLength={7}
//                           />
//                         </div>

//                         {/* Clear */}
//                         <button
//                           className={`pm-clear-btn${isClearDisabled ? " pm-clear-btn--disabled" : ""}`}
//                           onClick={isClearDisabled ? null : handleClearButtonClick}
//                           style={{
//                             height: 55, padding: "0 20px",
//                             background: isClearDisabled ? "#F3F4F6" : "linear-gradient(135deg,#F42020 0%,#D91A1A 100%)",
//                             color: isClearDisabled ? "#9CA3AF" : "#fff",
//                             border: "none", borderRadius: 12, fontSize: 15, fontWeight: 700,
//                             cursor: isClearDisabled ? "not-allowed" : "pointer", flexShrink: 0,
//                             boxShadow: isClearDisabled ? "none" : "0 4px 14px rgba(244,32,32,0.30)",
//                           }}
//                         >
//                           {t("Clear")}
//                         </button>
//                       </div>

//                       {/* Method amount pill — live as user types */}
//                       <div style={{
//                         display: "flex", alignItems: "center", justifyContent: "space-between",
//                         marginTop: 10, padding: "10px 16px",
//                         background: "linear-gradient(135deg,#EEF3FF,#F5F8FF)",
//                         borderRadius: 12, border: "1px solid #C7D6F8",
//                       }}>
//                         <span style={{ fontSize: 16, color: "#6B7280", fontWeight: 500 }}>
//                           {t(selectedPaymentMethod?.name)} {t("amount")}
//                         </span>
//                         <span style={{ fontSize: 20, fontWeight: 700, color: "#1b4597", fontFamily: "'DM Mono', monospace" }}>
//                           EGP {methodAmount.toFixed(2)}
//                         </span>
//                       </div>
//                     </div>

//                   ) : (
//                     /* ────────────────────────────────────────────────────────
//                        ORIGINAL TOTAL PAID INPUT
//                        Cash / Cash & Bank / COD / Credit
//                     ──────────────────────────────────────────────────────── */
//                     <div style={{ marginBottom: 14 }}>
//                       <SectionLabel>
//                         {selectedPaymentMethod?.name === "Cash & Bank" ? t("Total paid by cash") : t("Total Paid")}
//                       </SectionLabel>
//                       <div style={{ display: "flex", gap: 10 }}>
//                         <div
//                           className="pm-card"
//                           style={{
//                             display: "flex", alignItems: "center", flex: 1, height: 60,
//                             padding: "0 14px", gap: 8,
//                             border: "2px solid #1b4597", boxShadow: "0 0 0 3px rgba(27,69,151,0.10)",
//                             flexDirection: isRtl ? "row-reverse" : "row",
//                           }}
//                         >
//                           <span style={{ color: "#1b4597", fontSize: 14, fontWeight: 700, flexShrink: 0 }}>EGP</span>
//                           <input
//                             id="textInput1" ref={totalPaidInputRef}
//                             type="number" className="pm-input-no-arrows" autoFocus
//                             onFocus={(e) => { if (deviceType === "tablet") e.target.blur(); }}
//                             style={{
//                               flex: 1, border: "none", outline: "none", background: "transparent",
//                               fontSize: 22, fontWeight: 700, color: "#1F2937",
//                               fontFamily: "'DM Mono', monospace", direction: "ltr", textAlign: "left",
//                             }}
//                             value={grandTotal <= 0 ? "0" : totalPaid}
//                             readOnly={
//                               grandTotal < 0 ||
//                               selectedPaymentMethod?.name === "COD" ||
//                               selectedPaymentMethod?.name === "Credit"
//                             }
//                             onChange={(e) => {
//                               const sanitized = e.target.value.replace(/ز/g, ".").replace(/[^0-9.]/g, "");
//                               if (/^\d*\.?\d{0,2}$/.test(sanitized)) {
//                                 setTotalPaid(sanitized); setTenderCash(sanitized || 0);
//                                 const num   = parseFloat(sanitized) || 0;
//                                 const gtVal = parseFloat(String(grandTotal).replace(/[^0-9.]/g, "")) || 0;
//                                 setChange(`${Math.max(num - gtVal, 0)}`);
//                                 if (selectedPaymentMethod?.name === "Cash & Bank")
//                                   setRemainingAmount(Math.max(gtVal - num, 0));
//                               }
//                             }}
//                             maxLength={7}
//                           />
//                         </div>
//                         <button
//                           className={`pm-clear-btn${isClearDisabled ? " pm-clear-btn--disabled" : ""}`}
//                           onClick={isClearDisabled ? null : handleClearButtonClick}
//                           style={{
//                             height: 60, padding: "0 20px",
//                             background: isClearDisabled ? "#F3F4F6" : "linear-gradient(135deg,#F42020 0%,#D91A1A 100%)",
//                             color: isClearDisabled ? "#9CA3AF" : "#fff",
//                             border: "none", borderRadius: 12, fontSize: 15, fontWeight: 700,
//                             cursor: isClearDisabled ? "not-allowed" : "pointer", flexShrink: 0,
//                             boxShadow: isClearDisabled ? "none" : "0 4px 14px rgba(244,32,32,0.30)",
//                           }}
//                         >
//                           {t("Clear")}
//                         </button>
//                       </div>
//                     </div>
//                   )}

//                   {/* Return delivery fee */}
//                   {showReturnDelivery && (
//                     <div style={{ marginBottom: 14 }}>
//                       <SectionLabel>{t("Refund Delivery Fee")}</SectionLabel>
//                       <div
//                         className="pm-card"
//                         style={{
//                           display: "flex", alignItems: "center", height: 52, padding: "0 14px", gap: 8,
//                           border: errorMessage ? "2px solid #F42020" : "1px solid #EBEBF0",
//                           flexDirection: isRtl ? "row-reverse" : "row",
//                         }}
//                       >
//                         <span style={{ color: "#6B7280", fontSize: 14, fontWeight: 600 }}>EGP</span>
//                         <input
//                           id="textInput2" className="pm-input-no-arrows"
//                           value={returnDeliveryFee}
//                           style={{
//                             flex: 1, border: "none", outline: "none", background: "transparent",
//                             fontSize: 20, fontWeight: 700, color: "#1F2937",
//                             fontFamily: "'DM Mono', monospace", direction: "ltr", textAlign: "left",
//                           }}
//                           onChange={(e) => {
//                             const sanitized = e.target.value.replace(/ز/g, ".").replace(/[^0-9.]/g, "");
//                             if (/^\d*\.?\d{0,2}$/.test(sanitized)) {
//                               let val = sanitized.startsWith(".") ? "0" + sanitized : sanitized;
//                               val = val.replace(/^0+(?=\d)/, "") || "0";
//                               setErrorMessage(parseFloat(val) > constReturnDeliveryFee
//                                 ? `Value cannot exceed ${constReturnDeliveryFee}` : "");
//                               setReturnDeliveryFee(val);
//                             }
//                           }}
//                           onBlur={() => { if (returnDeliveryFee === 0) setReturnDeliveryFee(0); }}
//                           maxLength={7}
//                         />
//                       </div>
//                       {errorMessage && <p style={{ color: "#F42020", fontSize: 12, marginTop: 4 }}>{errorMessage}</p>}
//                     </div>
//                   )}

//                   {/* Delivery fee */}
//                   {(selectedOrderFrom?.name === "self_delivery" || selectedPaymentMethod?.name === "COD") && (
//                     <div style={{ marginBottom: 14 }}>
//                       <SectionLabel>{t("Delivery Fee")}</SectionLabel>
//                       <div className="pm-card" style={{
//                         display: "flex", alignItems: "center", height: 52, padding: "0 14px", gap: 8,
//                         flexDirection: isRtl ? "row-reverse" : "row",
//                       }}>
//                         <span style={{ color: "#6B7280", fontSize: 14, fontWeight: 600 }}>EGP</span>
//                         <input
//                           id="textInput3" type="number" className="pm-input-no-arrows" autoFocus
//                           value={deliveryFee}
//                           style={{
//                             flex: 1, border: "none", outline: "none", background: "transparent",
//                             fontSize: 20, fontWeight: 700, color: "#1F2937",
//                             fontFamily: "'DM Mono', monospace", direction: "ltr", textAlign: "left",
//                           }}
//                           onFocus={(e) => { if (deviceType === "tablet") e.target.blur(); }}
//                           onChange={(e) => {
//                             const sanitized = e.target.value.replace(/ز/g, ".").replace(/[^0-9.]/g, "");
//                             if (/^\d*\.?\d{0,2}$/.test(sanitized)) setDeliveryFee(sanitized);
//                           }}
//                           maxLength={7}
//                         />
//                       </div>
//                     </div>
//                   )}
//                 </div>
//               </div>

//               {/* Salesman */}
//               <div style={{ marginBottom: 16 }}>
//                 <div className="-mb-1"><SectionLabel>{t("Salesman")}</SectionLabel></div>
//                 <SelectBox
//                 width={'w-full'}
//                   name="salesman" defaultValue={null}
//                   onChange={(e) => setSelectedSalesman(e.target)}
//                   value={selectedSalesman}
//                   options={[
//                     { value: null, label: t("Select"), disabled: true },
//                     ...allSalesman?.map((item) => ({
//                       value: item.id,
//                       label: `${item.name} (${item?.phone_no})`,
//                     })),
//                   ]}
//                 />
//               </div>

//               {/* ── Keypad ── */}
//               <div style={{ direction: "ltr" }}>
//                 {/* Tiny indicator — shows what the keypad is currently filling */}
//                 {showCashAddon && (
//                   <div style={{
//                     display: "inline-flex", alignItems: "center", gap: 6,
//                     marginBottom: 8, padding: "4px 10px",
//                     background: "#F0FAE8", borderRadius: 8, border: "1px solid #C5E8A3",
//                   }}>
//                     <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#7EC242" }} />
//                     <span style={{ fontSize: 12, fontWeight: 600, color: "#5A9E2F" }}>
//                       {t("Keypad → Cash Amount")}
//                     </span>
//                   </div>
//                 )}

//                 {keypadValues.map((row, rowIndex) => (
//                   <div key={rowIndex} style={{ display: "flex", marginBottom: 10 }}>
//                     {row.map((item, index) => (
//                       <KeypadButton
//                         key={index} value={item.value}
//                         disabled={isKeypadDisabled}
//                         onClick={() => handleKeypadButtonClick(item.value)}
//                       >
//                         {item.value}
//                       </KeypadButton>
//                     ))}
//                   </div>
//                 ))}
//               </div>

//             </div>
//           </div>
//         </main>
//         <footer />
//       </section>
//     </div>
//   );
// }

// export default Payment_Modal;